<template>
  <div class="home catalogo-medico-lg">
    <!-- Sidenav Panel -->
    <div
      :style="{ display: sidenavOpen ? 'block' : 'none' }"
      class="fixed top-0 right-0 h-full w-full max-w-[800px] bg-white shadow-2xl z-[200] overflow-y-auto"
    >
      <div class="p-6">
        <!-- Close Button -->
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-gray-900">Detalles del Producto</h2>
          <button
            @click="closeSidenav"
            class="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Product Details -->
        <div v-if="selectedProduct">
          <div class="mb-6">
            <img
              :src="selectedProduct.image"
              :alt="selectedProduct.name"
              class="w-full h-48 object-contain rounded-lg"
            />
          </div>

          <h3 class="text-xl font-semibold mb-4 text-indigo-600">{{ selectedProduct.name }}</h3>

          <!-- Precio del producto en el detalle -->
          <ProductPrice 
            v-if="selectedProduct.pricing" 
            :pricing="selectedProduct.pricing" 
            class="mb-4"
          />

          <div class="space-y-4">
            <div>
              <h4 class="font-semibold text-gray-900 mb-2">Descripción</h4>
              <p class="text-gray-600 text-sm leading-relaxed">{{ selectedProduct.description }}</p>
            </div>
            <!-- Especificaciones y Características - Solo se muestra cuando showQuoteForm es false -->
            <div
              v-if="!showQuoteForm"
              class="flex flex-wrap bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 box-specification"
            >
              <div class="w-1/2 pr-4 text-left" v-if="selectedProduct.specifications">
                <h4 class="font-semibold text-gray-900 mb-2">Especificaciones</h4>
                <ul class="space-y-2">
                  <li
                    v-for="(value, key) in selectedProduct.specifications"
                    :key="key"
                    class="text-sm text-gray-600"
                  >
                    <span class="font-medium">{{ key }}:</span> {{ value }}
                  </li>
                </ul>
              </div>

              <div class="w-1/2 pr-4 text-left" v-if="selectedProduct.features">
                <h4 class="font-semibold text-gray-900 mb-2">Características</h4>
                <ul class="space-y-1">
                  <li
                    v-for="(feature, index) in selectedProduct.features"
                    :key="index"
                    class="text-sm text-gray-600"
                  >
                    • {{ feature }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Aplicaciones - Solo se muestra cuando showQuoteForm es false -->
            <div
              v-if="!showQuoteForm"
              class="flex flex-wrap bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4"
            >
              <div class="w-full pr-4 text-left" v-if="selectedProduct.applications">
                <h4 class="font-semibold text-gray-900 mb-2">Aplicaciones</h4>
                <ul class="space-y-1">
                  <li
                    v-for="(app, index) in selectedProduct.applications"
                    :key="index"
                    class="text-sm text-gray-600"
                  >
                    • {{ app }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Formulario de Cotización - Solo se muestra cuando showQuoteForm es true -->
            <div
              v-if="showQuoteForm"
              class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6"
            >
              <h4 class="font-semibold text-gray-900 mb-4 text-center">Solicitar Cotización</h4>
              <form @submit.prevent="submitQuote" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Nombre completo *</label
                  >
                  <input
                    v-model="quoteForm.nombre"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ingrese su nombre completo"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                  <input
                    v-model="quoteForm.telefono"
                    type="tel"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ingrese su número de teléfono"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Correo electrónico *</label
                  >
                  <input
                    v-model="quoteForm.correo"
                    type="email"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ingrese su correo electrónico"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Producto</label>
                  <input
                    v-model="quoteForm.producto"
                    type="text"
                    readonly
                    class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1"
                    >Comentarios adicionales</label
                  >
                  <textarea
                    v-model="quoteForm.comentario"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ingrese cualquier comentario o requerimiento específico"
                  ></textarea>
                </div>
                <div class="flex space-x-3">
                  <button
                    type="submit"
                    :disabled="isSubmittingQuote"
                    class="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ isSubmittingQuote ? "Enviando..." : "Enviar Cotización" }}
                  </button>
                  <button
                    type="button"
                    @click="resetQuoteForm"
                    class="flex-1 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-semibold"
                  >
                    Limpiar
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div class="mt-8 pt-6 border-t border-gray-200">
            <button
              @click="toggleQuoteForm"
              class="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
            >
              {{ showQuoteForm ? "Ver Especificaciones" : "Cotizar" }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <NavBar />
    <!-- Hero Section -->
    <section class="pb-16 bg-optimuz">
      <div class="pt-32">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="mx-auto max-w-2xl lg:text-center sectionAnimate">
            <h2 class="text-base/7 font-semibold text-indigo-600">
              Nuestras Soluciones Innovadoras
            </h2>
            <p
              class="mt-2 text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-balance text-white"
            >
              Productos Medicos
            </p>
          </div>
        </div>
      </div>
    </section>
    <!-- Revisión Clínica Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12 text-gray-900">
          Monitor de Revisión Clínica
        </h2>
        <div class="max-w-6xl mx-auto">
          <div class="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-8 mb-8">
            <!-- MONITOR 1 -->
            <div
              class="rounded bg-gray-300 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xs"
            >
              <a
                href="#"
                @click.prevent="openSidenav('24HR513C', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Clinical/8-24HR513C-monitor-clinico-24-2mp-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Clinical/8-24HR513C-monitor-clinico-24-2mp-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de revisión clínica IPS LG de 24» y 2 MP
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    ha sido diseñado para ofrecer una visualización médica clara y precisa,
                    asegurando imágenes nítidas y una experiencia de usuario optimizada en entornos
                    clínicos.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['24HR513C'].pricing" />
              </div>
            </div>
            <!-- MONITOR 2 -->
            <div
              class="rounded bg-gray-300 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xs"
            >
              <a
                href="#"
                @click.prevent="openSidenav('27HJ712C', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Clinical/7-27HJ712C-monitor-clinico-27-8mp-1-700x525.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Clinical/7-27HJ712C-monitor-clinico-27-8mp-2-700x525.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de revisión clínica IPS LG de 27» y 8 MP
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor de Revisión Clínica IPS LG de 27» y 8 MP está diseñado para
                    proporcionar imágenes médicas de alta calidad con una visualización precisa y
                    estable.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['27HJ712C'].pricing" />
              </div>
            </div>
            <!-- MONITOR 3 -->
            <div
              class="rounded bg-gray-300 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xs"
            >
              <a
                href="#"
                @click.prevent="openSidenav('27HJ713C', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Clinical/6-27HJ713C-B-monitor-clinico-27-8mp-1-700x525.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Clinical/6-27HJ713C-B-monitor-clinico-27-8mp-2-700x525.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de revisión clínica White IPS LG de 27» y 8 MP
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    proporciona una representación precisa de las imágenes médicas. Su amplio ángulo
                    de visión de 178° permite a médicos y pacientes observar las imágenes sin
                    distorsiones ni alteraciones de color.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['27HJ713C'].pricing" />
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-1">
            <div class="shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8">
              <h3 class="text-2xl font-semibold mb-6 text-center">Características Avanzadas</h3>

              <div class="space-y-4">
                <div class="grid gap-4 grid-cols-2">
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div class="">
                      <h5 class="font-semibold text-gray-900 text-left">DICOM Parte 14:</h5>
                      <p class="text-gray-600 text-sm text-left">
                        Calibración precisa de la escala de grises para la revisión de estudios
                        médicos, asegurando imágenes médicas de alta fidelidad.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Estabilización del brillo:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Gracias al sensor de luminosidad, el monitor mantiene una luminancia estable
                        durante el tiempo de uso, evitando fluctuaciones que puedan afectar el
                        diagnóstico.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Modo de Vista de Enfoque y Patología:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Resalta áreas específicas de la imagen médica, mejorando la precisión en el
                        análisis de detalles clínicos fundamentales.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Seguridad contra parpadeo:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Protege la vista de los profesionales de la salud, reduciendo la fatiga
                        ocular y mejorando la comodidad en jornadas prolongadas de trabajo.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Brillo de hasta 600 cd/m²:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Proporciona una iluminación uniforme para una visualización más clara y
                        precisa, sin distorsiones en tonos grises.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Pivote bidireccional y ajuste ergonómico:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Facilita la adaptabilidad a diferentes entornos de trabajo, mejorando la
                        eficiencia del personal médico.
                      </p>
                    </div>
                  </div>
                </div>
                <p class="text-gray-700 mt-4 italic">
                  Con estos monitores, los especialistas pueden evaluar imágenes de diagnóstico con
                  la máxima precisión y sin perder ningún detalle clave, lo que contribuye a
                  diagnósticos más rápidos y confiables.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Diagnóstico Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12 text-gray-900">Monitor de Diagnóstico</h2>
        <div class="max-w-6xl mx-auto">
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <!-- MONITOR Diagnostico 1 -->
            <div
              class="rounded bg-gray-300 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xs"
            >
              <a
                href="#"
                @click.prevent="openSidenav('21HQ513D', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/2-21HQ513D-monitor-diagnostico-21-3mp-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/2-21HQ513D-monitor-diagnostico-21-3mp-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de diagnóstico IPS LG de 21,3» y 3 MP
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor de Diagnóstico IPS LG de 21,3» y 3 MP es una solución profesional
                    diseñada para ofrecer precisión y claridad en la revisión de imágenes médicas.
                    Con tecnología avanzada y un diseño ergonómico, este monitor optimiza los flujos
                    de trabajo en hospitales, clínicas y centros de diagnóstico.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['21HQ513D'].pricing" />
              </div>
            </div>
            <!-- MONITOR Diagnostico 2 -->
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('32HL512D', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/1-32HL512D-monitor-diagnostico-31-8mp-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/1-32HL512D-monitor-diagnostico-31-8mp-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de diagnóstico de 31,5″ y 8 MP
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor de Diagnóstico LG de 8 MP es una solución avanzada diseñada para
                    mejorar la precisión en la interpretación de imágenes médicas en hospitales,
                    clínicas y centros de teleradiología. Con tecnología de última generación y una
                    pantalla IPS de 31,5 pulgadas, proporciona una calidad de imagen superior y una
                    ergonomía optimizada para largas jornadas de trabajo. Además, su resolución
                    múltiple facilita su adaptabilidad a diversas modalidades de imagen.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['32HL512D'].pricing" />
              </div>
            </div>
            <!-- MONITOR Diagnostico 3 -->
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('21HQ613D', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/4-21HQ613D-monitor-diagnostico-21-5mp-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/4-21HQ613D-monitor-diagnostico-21-5mp-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de diagnóstico IPS LG de 21,3″ y 5 MP para mamografía
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor de Diagnóstico IPS LG de 21,3″ y 5 MP para Mamografía ha sido
                    diseñado específicamente para la interpretación de imágenes médicas de mama con
                    una precisión excepcional. Gracias a su tecnología avanzada, garantiza la
                    visualización clara y detallada de mamografías digitales, tomosíntesis mamaria y
                    otras modalidades de imagenología.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['21HQ613D'].pricing" />
              </div>
            </div>
            <!-- MONITOR Diagnostico 4 -->
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('32HQ713D', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/3-32HQ713D-monitor-diagnostico-31-8mp-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/3-32HQ713D-monitor-diagnostico-31-8mp-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de Diagnóstico LG IPS de 31,5 pulgadas y 8 MP en color negro
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor de Diagnóstico LG IPS de 31,5 pulgadas y 8 MP en color negro es una
                    solución de vanguardia diseñada para garantizar una visualización precisa y
                    confiable en entornos médicos. Gracias a su panel IPS de alta resolución, brinda
                    una claridad excepcional en la interpretación de imágenes médicas, optimizando
                    la eficiencia de los profesionales de la salud.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['32HQ713D'].pricing" />
              </div>
            </div>
            <!-- MONITOR Diagnostico 5 -->
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('31HN713D', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/5-31HN713D-monitor-diagnostico-31-12mp-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Diagnostico/5-31HN713D-monitor-diagnostico-31-12mp-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor de Diagnóstico para Mamografía de 31 pulgadas y 12 MP
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor de Diagnóstico para Mamografía de 31 pulgadas y 12 MP está diseñado
                    para ofrecer imágenes nítidas y de alta precisión, facilitando la interpretación
                    de mamografías digitales y tomosíntesis mamaria. Su tecnología avanzada y su
                    diseño ergonómico lo convierten en una herramienta imprescindible en entornos de
                    diagnóstico médico.
                  </p>
                </div>
              </a>
              
              <!-- Pricing Component -->
              <div class="mt-3 mx-4 pb-4">
                <ProductPrice :pricing="products['31HN713D'].pricing" />
              </div>
            </div>
          </div>
          <div class="bg-white rounded-lg shadow-lg p-8">
            <h3 class="text-2xl font-semibold mb-6 text-center">Características Avanzadas</h3>
            <div class="space-y-4">
              <div class="grid gap-4 grid-cols-2">
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div class="">
                    <h5 class="font-semibold text-gray-900 text-left">
                      Modos multiresolución (8/6/4 MP, 5/3/2 MP y 12/6 MP):
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      que permiten adaptar la visualización a las necesidades específicas de cada
                      estudio.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">
                      Brillo optimizado (hasta 1200 cd/m²):
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      para mejorar el contraste y la legibilidad de las imágenes médicas en entornos
                      clínicos.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">
                      Modo de Patología y Modo de Vista de Enfoque:
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      que potencian el análisis de detalles microscópicos y resaltan áreas de
                      interés en las imágenes.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">
                      Diseño ergonómico y conectividad avanzada:
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      con opciones como Daisy Chain y sensor de luminosidad para garantizar imágenes
                      estables y sin variaciones en el tiempo.
                    </p>
                  </div>
                </div>
              </div>
              <p class="text-gray-700 mt-4 italic">
                Por otro lado, los monitores de mamografía han sido desarrollados con
                características especializadas para la evaluación de imágenes mamarias. Gracias al
                sensor de luminosidad, pueden ajustar automáticamente el brillo y la escala de
                grises, permitiendo visualizar estructuras finas y detectar anomalías con mayor
                claridad. Su integración con DICOM Parte 14 garantiza la reproducción fiel de los
                tonos grises, brindando un soporte diagnóstico confiable en la detección temprana
                del cáncer de mama.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Quirúrgico Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12 text-gray-900">Monitor Quirúrgico</h2>
        <div class="max-w-6xl mx-auto">
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('27HQ710S', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/13-27HQ710S-monitor-quirurgico-4k-27-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/13-27HQ710S-monitor-quirurgico-4k-27-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor quirúrgico 4K de 27 pulgadas con mini-LED
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor quirúrgico 4K de 27 pulgadas con mini-LED de LG está diseñado para
                    proporcionar imágenes ultra precisas y alta fidelidad cromática, esenciales en
                    entornos quirúrgicos exigentes. Con tecnología avanzada Mini-LED, HDR10 y sRGB
                    139 %, este monitor optimiza la visualización para procedimientos quirúrgicos de
                    alta precisión.
                  </p>
                </div>
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('32HL710S', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/12-32HL710S-monitor-quirurgico-4k-31-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/12-32HL710S-monitor-quirurgico-4k-31-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Precisión y Tecnología para el Quirófano
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor quirúrgico IPS 4K de 31,5» de LG está diseñado para proporcionar
                    imágenes de alta resolución con una fidelidad cromática excepcional,
                    garantizando una visualización óptima en procedimientos quirúrgicos. Gracias a
                    su tecnología avanzada, este monitor es una herramienta indispensable en
                    entornos médicos de alta exigencia.
                  </p>
                </div>
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('32HL714S', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/11-32HL714S-monitor-quirurgico-4k-31-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/11-32HL714S-monitor-quirurgico-4k-31-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor Quirúrgico IPS 4K de 31,5» de LG
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor Quirúrgico IPS 4K de 31,5» de LG es una herramienta imprescindible
                    para la visualización de imágenes médicas en alta resolución durante
                    procedimientos quirúrgicos. Su tecnología IPS y resolución 4K garantizan
                    detalles nítidos, colores precisos y una visualización sin distorsiones desde
                    cualquier ángulo.
                  </p>
                </div>
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('55MH5K', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/9-55MH5K-monitor-quirurgico-4k-55-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/9-55MH5K-monitor-quirurgico-4k-55-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor Quirúrgico IPS 4K de 55» de LG
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor Quirúrgico IPS 4K de 55» de LG ofrece una solución avanzada para
                    visualizar imágenes en tiempo real durante procedimientos quirúrgicos. Gracias a
                    su resolución ultra HD y su compatibilidad con HDR10, proporciona una claridad
                    de imagen superior, asegurando precisión en cada intervención.
                  </p>
                </div>
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('27HK510S', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/14-27HK510S-monitor-quirurgico-fullhd-27-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Monitor-Quirurjico/14-27HK510S-monitor-quirurgico-fullhd-27-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Monitor quirúrgico IPS Full HD de 27» de LG
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Monitor quirúrgico IPS Full HD de 27» de LG ha sido diseñado para ofrecer una
                    visualización de alta calidad en entornos quirúrgicos, proporcionando imágenes
                    nítidas y precisas para una mejor toma de decisiones en procedimientos médicos.
                    Con una tecnología de pantalla IPS optimizada y características de seguridad
                    avanzadas, este monitor es la opción ideal para quirófanos y centros de cirugía.
                  </p>
                </div>
              </a>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-1">
            <div class="shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8">
              <h3 class="text-2xl font-semibold mb-6 text-center">Características Avanzadas</h3>

              <div class="space-y-4">
                <div class="grid gap-4 grid-cols-2">
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div class="">
                      <h5 class="font-semibold text-gray-900 text-left">
                        Compatibilidad con HDR10:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Mejora la profundidad de color y el contraste para una visualización más
                        realista.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">Soporte 12G-SDI:</h5>
                      <p class="text-gray-600 text-sm text-left">
                        Permite la transmisión de video de alta calidad con latencia ultra baja.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">Modo espejo y rotación:</h5>
                      <p class="text-gray-600 text-sm text-left">
                        ROptimiza la visualización desde diferentes ángulos quirúrgicos.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">Hasta 4PBP y PIP:</h5>
                      <p class="text-gray-600 text-sm text-left">
                        Permite la visualización simultánea de hasta cuatro fuentes de imagen.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Vidrio protector con tecnología antirreflejo y antihuellas:
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Reduce el deslumbramiento y facilita la limpieza.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                    >
                      <span class="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900 text-left">
                        Resistencia al agua y polvo (IP45 frontal, IP32 trasero):
                      </h5>
                      <p class="text-gray-600 text-sm text-left">
                        Asegura durabilidad en ambientes médicos rigurosos.
                      </p>
                    </div>
                  </div>
                </div>
                <p class="text-gray-700 mt-4 italic">
                  Gracias a estas tecnologías, los cirujanos pueden operar con total confianza,
                  reduciendo errores y mejorando la eficiencia del procedimiento.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Detectores Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-12 text-gray-900">
          Mini Digital X-ray Detector
        </h2>
        <div class="max-w-6xl mx-auto">
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('17HQ701G', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Detector-Rayos/16-17HQ701G-detector-rayos-x-a-si-tft-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Detector-Rayos/16-17HQ701G-detector-rayos-x-a-si-tft-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Detector de rayos X digital LG con a-Si TFT
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Detector de rayos X digital LG con a-Si TFT es una solución innovadora
                    diseñada para proporcionar imágenes radiológicas de alta calidad en tiempo real.
                    Gracias a su avanzada tecnología TFT de silicio amorfo, este detector garantiza
                    una captación eficiente de imágenes con un rendimiento optimizado para
                    hospitales y centros de diagnóstico.
                  </p>
                </div>
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('10HQ701G', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Detector-Rayos/18-10HQ701G-detector-rayos-x-a-si-tft-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Detector-Rayos/18-10HQ701G-detector-rayos-x-a-si-tft-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Detector de rayos X digital LG con a-Si TFT
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Detector de rayos X digital LG con pantalla TFT a-Si ha sido diseñado para
                    proporcionar imágenes médicas de alta calidad con tiempos de procesamiento
                    rápidos y una estructura robusta. Gracias a su avanzada tecnología de silicio
                    amorfo (a-Si), garantiza diagnósticos más precisos y eficientes en diferentes
                    entornos clínicos.
                  </p>
                </div>
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-md p-6">
              <a
                href="#"
                @click.prevent="openSidenav('14HQ701G', $event)"
                class="group block cursor-pointer"
              >
                <div class="relative h-[280px] sm:h-[240px]">
                  <img
                    src="../assets/images/lg/Detector-Rayos/18-10HQ701G-detector-rayos-x-a-si-tft-1.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-100 group-hover:opacity-0 rounded"
                  />

                  <img
                    src="../assets/images/lg/Detector-Rayos/18-10HQ701G-detector-rayos-x-a-si-tft-2.jpg"
                    alt=""
                    class="absolute inset-0 h-full w-full object-contain opacity-0 group-hover:opacity-100 rounded"
                  />
                </div>

                <div class="mt-3">
                  <h3
                    class="text-md text-gray-700 group-hover:underline group-hover:underline-offset-4"
                  >
                    Detector de rayos X digital LG con pantalla TFT a-Si
                  </h3>

                  <p class="mt-1.5 text-xs text-pretty text-gray-500 mx-4">
                    El Detector de rayos X digital LG con pantalla TFT a-Si es una solución
                    innovadora diseñada para proporcionar imágenes radiológicas de alta calidad en
                    tiempo real. Gracias a su avanzada tecnología TFT de silicio amorfo, este
                    detector garantiza una captación eficiente de imágenes con un rendimiento
                    optimizado para hospitales y centros de diagnóstico.
                  </p>
                </div>
              </a>
            </div>
          </div>
          <div class="bg-white rounded-lg shadow-lg p-8">
            <h3 class="text-2xl font-semibold mb-6 text-center">Características Avanzadas</h3>
            <div class="space-y-4">
              <div class="grid gap-4 grid-cols-2">
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div class="">
                    <h5 class="font-semibold text-gray-900 text-left">
                      Alta resolución y precisión:
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      Gracias a su tamaño de píxel de 140μm y conversión A/D de 16 bits, ofrece
                      imágenes médicas detalladas con una claridad sin precedentes.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">
                      Procesamiento ultrarrápido:
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      Obtención de imágenes en 1,5 segundos con conexión por cable y 2,5 segundos de
                      manera inalámbrica.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">
                      Alta resistencia y durabilidad:
                    </h5>
                    <p class="text-gray-600 text-sm text-left">
                      Diseñado con cuerpo de magnesio y fibra de carbono, con protección IP68 contra
                      agua y polvo.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">Carga uniforme y puntual:</h5>
                    <p class="text-gray-600 text-sm text-left">
                      Soporta hasta 400 kg de carga uniforme y 200 kg de carga puntual, lo que
                      garantiza resistencia en cualquier entorno clínico.
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-3">
                  <div
                    class="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                  >
                    <span class="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h5 class="font-semibold text-gray-900 text-left">Portabilidad eficiente:</h5>
                    <p class="text-gray-600 text-sm text-left">
                      Diseño liviano y ergonómico que facilita su transporte y uso en distintas
                      aplicaciones médicas.
                    </p>
                  </div>
                </div>
              </div>
              <p class="text-gray-700 mt-4 italic">
                Los Detectores Digitales de Rayos X destacan por su tecnología de vanguardia en
                captura de imágenes, asegurando diagnósticos más certeros y eficientes. Con su
                sensor de alta sensibilidad, permiten visualizar estructuras óseas y tejidos blandos
                con gran definición, reduciendo la necesidad de repeticiones en los estudios.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer section -->
    <footer>
      <div class="bg-gray-100">
        <div
          class="max-w-screen-lg py-10 px-4 sm:px-6 text-gray-800 sm:flex justify-between mx-auto"
        >
          <div class="p-5 sm:w-2/12 border-r">
            <div class="text-sm uppercase text-indigo-600 font-bold">Menu</div>
            <ul>
              <li class="my-2">
                <a class="hover:text-indigo-600" href="#inicio">Inicio</a>
              </li>
              <li class="my-2">
                <a class="hover:text-indigo-600" href="#productos">Productos y Servicios</a>
              </li>
              <li class="my-2">
                <a class="hover:text-indigo-600" href="#nosotros">Nosotros</a>
              </li>
              <li class="my-2">
                <a class="hover:text-indigo-600" href="#contacto">Contacto</a>
              </li>
            </ul>
          </div>
          <div class="p-5 sm:w-7/12 border-r text-center">
            <h3 class="font-bold text-xl text-indigo-600 mb-4">OPTIMUZ SPA</h3>
            <h1 class="text-gray-500 text-sm mb-10 text-justify">
              Nuestra solución MRIS y MPACS permite la optimización de la gestión de estudios de
              imagenología, mejorando la programación de citas, el seguimiento de pacientes, la
              interpretación de imágenes y la entrega de resultados. Con nuestra plataforma, aumente
              la eficiencia, precisión y accesibilidad de los servicios de radiología, ofreciendo
              una atención médica más ágil y precisa.
            </h1>
          </div>
          <div class="p-5 sm:w-3/12">
            <div class="text-sm uppercase text-indigo-600 font-bold">Información</div>
            <ul>
              <li class="my-2">
                <a class="hover:text-indigo-600">Compañía de Jesús 1390 Of. 2204, Santiago</a>
              </li>
              <li class="my-2">
                <a
                  class="hover:text-indigo-600 hover:text-indigo-600"
                  href="mailto:contacto@optimuz.cl"
                  >contacto@optimuz.cl
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div
          class="flex py-5 m-auto text-gray-800 text-sm flex-col items-center border-t max-w-screen-xl"
        >
          <div class="my-5">© Copyright 2025. All Rights Reserved OPTIMUZ SPA.</div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { API_CREDENTIALS } from "../config/api";
import NavBar from "@/components/NavBarPageExterna.vue";
import ProductPrice from "@/components/ProductPrice.vue";
import "../assets/main.css";

export default {
  name: "ProductosMedicos",
  components: {
    NavBar,
    ProductPrice,
  },
  data() {
    return {
      sidenavOpen: false,
      selectedProduct: null,
      showQuoteForm: false,
      isSubmittingQuote: false,
      quoteForm: {
        nombre: "",
        telefono: "",
        correo: "",
        comentario: "",
        producto: "",
      },
      products: {
        // Monitores de Revisión Clínica
        "24HR513C": {
          name: 'Monitor de Revisión Clínica IPS LG de 24" y 2 MP',
          category: "Monitor de Revisión Clínica",
          image: require("../assets/images/lg/Monitor-Clinical/8-24HR513C-monitor-clinico-24-2mp-1.jpg"),
          description:
            'Precisión y Eficiencia para la Revisión Clínica. El Monitor de Revisión Clínica IPS LG de 24" y 2 MP ha sido diseñado para ofrecer una visualización médica clara y precisa, asegurando imágenes nítidas y una experiencia de usuario optimizada en entornos clínicos. Gracias a su alto brillo, calibración avanzada y diseño ergonómico, se convierte en una herramienta indispensable para profesionales de la salud.',
          pricing: {
            originalPrice: 1238095,
            discountedPrice: 1114286,
            discountPercentage: 10,
            installments: {
              count: 12,
              amount: 92857
            }
          },
          specifications: {
            Tamaño: "24 pulgadas",
            Resolución: "1920 x 1200 (2MP)",
            "Relación de Aspecto": "16:10",
            "Brillo (Típico)": "600 cd/m²",
            "Brillo (Estabilización)": "350 cd/m²",
            Contraste: "1000:1",
            "Tiempo de Respuesta": "5ms (GtG en Faster)",
            "Ángulos de Visión": "178°/178°",
            "Bit de Color": "8bit",
            "Gama de Colores": "NTSC 70% (CIE1931)",
            "Paso de Píxeles": "0.27 x 0.27 mm",
            "Tratamiento de Superficie": "Anti-Reflejo",
            Conectividad: "HDMI, DisplayPort, DVI-D, USB 3.0",
            "Consumo de Energía (Máx.)": "65W",
            "Dimensiones con Soporte": "574.2 x 559.4 x 248.2 mm",
            "Peso con Soporte": "7.1 kg",
          },
          benefits: {
            "Alto Brillo para Diagnósticos Precisos":
              "Con un brillo de 600 cd/m², este monitor cumple con los estándares del Colegio Americano de Radiología (ACR) para monitores de diagnóstico, garantizando una calidad de imagen adecuada para la revisión clínica y la evaluación médica.",
            "Calibración Automática para Imágenes Consistentes":
              "El sensor de calibración frontal interno mantiene la precisión de las imágenes sin necesidad de equipos adicionales, asegurando que los niveles de escala de grises y el brillo sean consistentes en todo momento.",
            "Modo de Vista de Enfoque para Mayor Precisión":
              "El modo Focus View permite a los médicos destacar áreas específicas de la imagen médica, atenuando el resto de la pantalla para mejorar la concentración en detalles clave del estudio.",
            "Modo Patología: Claridad en la Observación":
              "Gracias al modo de patología, el monitor ofrece una calidad de imagen comparable a la observación a través de un microscopio, permitiendo diagnósticos más precisos y confiables.",
          },
          workflow: {
            "Gestión con LCS Medical":
              "El software LCS Medical facilita la calibración y el control de calidad del monitor conforme a los estándares DICOM Parte 14, asegurando una visualización precisa de las imágenes médicas y cumpliendo con regulaciones internacionales como AAPM TG18, ACR, DIN-6868-157, IEC 62563-1 y RANZCR.",
            "Conexión en Cadena para un Espacio de Trabajo Organizado":
              "Con la tecnología Daisy Chain, este monitor permite una conexión fluida con otros dispositivos, facilitando la integración con monitores de diagnóstico de mayor resolución y reduciendo el desorden en el área de trabajo.",
          },
          ergonomics: {
            "Compatibilidad con Múltiples Interfaces":
              'El Monitor de Revisión Clínica IPS LG de 24" y 2 MP es compatible con una variedad de dispositivos gracias a sus entradas HDMI, DisplayPort y DVI-D, asegurando conectividad sin problemas con diferentes equipos médicos.',
            "Estación de Trabajo Personalizable": {
              "Pivote Automático Bidireccional":
                "Para cambiar entre orientación vertical y horizontal",
              Giro: "±45° para adaptarse a cualquier ángulo de visión",
              "Altura ajustable": "0 a 150 mm para una postura de trabajo óptima",
              Inclinación: "-5° a 25° para reducir la fatiga visual",
            },
            "Sensor de Luminancia Automático":
              "Este monitor está equipado con un sensor de luminancia automático que ajusta el brillo de la pantalla de acuerdo con las condiciones de iluminación ambiental, mejorando la experiencia de visualización y reduciendo la fatiga ocular durante largas jornadas de trabajo.",
          },
          features: [
            "Alto Brillo de 600 cd/m² cumple estándares ACR",
            "Calibración Automática con sensor frontal interno",
            "Modo Focus View para destacar áreas específicas",
            "Modo Patología para claridad microscópica",
            "Estabilización de Brillo automática",
            "Compatible con DICOM Parte 14",
            "Pivote Automático Bidireccional (±90°)",
            "Altura ajustable (0-150mm)",
            "Inclinación (-5° a 25°) y Giro (±45°)",
            "Sensor de Luminancia Automático",
            "Tecnología Daisy Chain",
            "Calibración de Hardware integrada",
            "Estabilizador de Negros",
            "Seguro contra Parpadeo",
            "Ahorro de Energía Inteligente",
          ],
          applications: [
            "Revisión clínica de imágenes médicas",
            "Radiología y teleradiología",
            "Evaluaciones en hospitales y clínicas",
            "Investigación médica y educación",
            "Diagnósticos de precisión",
            "Patología digital",
            "Gestión con software LCS Medical",
          ],
          purchase_info: {
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            Proceso:
              "Proceso de compra ágil y sin complicaciones para hospitales, clínicas y centros de diagnóstico",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades",
          },
        },
        "27HJ712C": {
          name: 'Monitor de Revisión Clínica IPS LG de 27" y 8 MP',
          category: "Monitor de Revisión Clínica",
          image: require("../assets/images/lg/Monitor-Clinical/7-27HJ712C-monitor-clinico-27-8mp-1-700x525.jpg"),
          description:
            'Precisión y Calidad en Revisión Clínica. El Monitor de Revisión Clínica IPS LG de 27" y 8 MP está diseñado para proporcionar imágenes médicas de alta calidad con una visualización precisa y estable. Su tecnología avanzada facilita la interpretación de estudios clínicos, asegurando eficiencia y comodidad para los profesionales de la salud.',
          pricing: {
            originalPrice: 1579048,
            discountedPrice: 1421143,
            discountPercentage: 10,
            installments: {
              count: 12,
              amount: 118429
            }
          },
          specifications: {
            Tamaño: "27 pulgadas",
            Resolución: "2560 x 2048 (8MP)",
            Brillo: "350 nits",
            "Cobertura de Color": "sRGB 99%",
            "Ángulos de Visión": "178° (horizontal y vertical)",
            Panel: "IPS",
            "Estabilización de Brillo": "Sensor integrado",
            Cumplimiento: "DICOM Parte 14",
            "Tecnología de Protección": "Flicker Safe",
            Ergonomía: "Altura ajustable, Pivote, Inclinación, Rotación",
          },
          benefits: {
            "Calidad de Imagen Superior para Evaluaciones Clínicas":
              "El panel IPS de 27 pulgadas y 8 MP proporciona una representación precisa de las imágenes médicas. Su amplio ángulo de visión de 178° permite a médicos y pacientes observar las imágenes sin distorsiones ni alteraciones de color.",
            "Más Brillante y Más Preciso":
              "Gracias a un brillo de 350 nits y a la cobertura sRGB del 99%, este monitor ofrece una expresión de color más brillante y detallada, optimizando la precisión de los estudios clínicos.",
            "Estabilización del Brillo para Mayor Consistencia":
              "Un sensor integrado regula la estabilidad del brillo, compensando automáticamente las variaciones causadas por el envejecimiento de la pantalla. Esto garantiza una calidad de imagen constante en el tiempo.",
            "Cumplimiento de Estándares Médicos con DICOM Parte 14":
              "El DICOM Parte 14 Gamma ajusta automáticamente los niveles de escala de grises en imágenes médicas, asegurando una visualización uniforme y precisa para el diagnóstico y la revisión clínica.",
          },
          comfort_safety: {
            "Protección Ocular con Tecnología Flicker Safe":
              "El modo Flicker Safe minimiza el parpadeo de la pantalla, reduciendo el cansancio ocular durante largas horas de trabajo. Esto protege la visión de los profesionales médicos y optimiza su productividad.",
          },
          ergonomics: {
            "Ergonomía y Adaptabilidad":
              'El diseño ergonómico del Monitor de Revisión Clínica IPS LG de 27" y 8 MP permite ajustar la pantalla según las necesidades del usuario:',
            "Características de Ajuste": {
              "Altura ajustable": "Para una postura cómoda",
              "Pivote de dos vías": "Facilitando la orientación vertical u horizontal",
              "Inclinación y rotación": "Adaptándose a diferentes ángulos de visión",
            },
          },
          features: [
            "Panel IPS de 27 pulgadas con resolución 8MP",
            "Brillo de 350 nits para mayor claridad",
            "Cobertura sRGB del 99% para colores precisos",
            "Ángulo de visión de 178° sin distorsiones",
            "Estabilización automática del brillo",
            "Compatible con DICOM Parte 14",
            "Tecnología Flicker Safe para protección ocular",
            "Altura ajustable para ergonomía",
            "Pivote de dos vías (vertical/horizontal)",
            "Inclinación y rotación ajustables",
            "Sensor integrado de estabilización",
            "Gamma DICOM automático",
            "Diseño ergonómico avanzado",
          ],
          applications: [
            "Revisión de imágenes clínicas",
            "Radiología y teleradiología",
            "Evaluaciones en consultorios y hospitales",
            "Investigación médica y educación",
            "Diagnósticos de precisión",
            "Interpretación de estudios médicos",
            "Visualización de imágenes DICOM",
          ],
          purchase_info: {
            Financiamiento:
              "Opciones de pago flexibles, incluyendo tarjeta de crédito con financiamiento hasta 12 meses",
            Asesoría:
              "Asesoría especializada para encontrar la mejor solución en tecnología médica",
            Contacto: "Contáctanos para recibir asesoría personalizada y una cotización a medida",
          },
        },
        "27HJ713C": {
          name: 'Monitor de Revisión Clínica Blanco IPS LG de 27" y 8 MP',
          category: "Monitor de Revisión Clínica",
          image: require("../assets/images/lg/Monitor-Clinical/6-27HJ713C-B-monitor-clinico-27-8mp-1-700x525.jpg"),
          description:
            'Precisión y Calidad en Revisión Clínica. El Monitor de Revisión Clínica IPS LG de 27" y 8 MP está diseñado para proporcionar imágenes médicas de alta calidad con una visualización precisa y estable. Su tecnología avanzada facilita la interpretación de estudios clínicos, asegurando eficiencia y comodidad para los profesionales de la salud.',
          pricing: {
            originalPrice: 1579048,
            discountedPrice: 1421143,
            discountPercentage: 10,
            installments: {
              count: 12,
              amount: 118429
            }
          },
          specifications: {
            Tamaño: "27 pulgadas",
            Resolución: "2560 x 2048 (8MP)",
            Panel: "IPS",
            Brillo: "350 nits",
            "Cobertura de Color": "sRGB 99%",
            "Ángulos de Visión": "178° (horizontal y vertical)",
            "Estabilización de Brillo": "Sensor integrado",
            Cumplimiento: "DICOM Parte 14",
            "Tecnología de Protección": "Flicker Safe",
            Ergonomía: "Altura ajustable, Pivote, Inclinación, Rotación",
            Color: "Blanco",
          },
          benefits: {
            "Calidad de Imagen Superior para Evaluaciones Clínicas":
              "El panel IPS de 27 pulgadas y 8 MP proporciona una representación precisa de las imágenes médicas. Su amplio ángulo de visión de 178° permite a médicos y pacientes observar las imágenes sin distorsiones ni alteraciones de color.",
            "Más Brillante y Más Preciso":
              "Gracias a un brillo de 350 nits y a la cobertura sRGB del 99%, este monitor ofrece una expresión de color más brillante y detallada, optimizando la precisión de los estudios clínicos.",
            "Cumplimiento de Estándares Médicos con DICOM Parte 14":
              "El DICOM Parte 14 Gamma ajusta automáticamente los niveles de escala de grises en imágenes médicas, asegurando una visualización uniforme y precisa para el diagnóstico y la revisión clínica.",
            "Estabilización del Brillo para Mayor Consistencia":
              "Un sensor integrado regula la estabilidad del brillo, compensando automáticamente las variaciones causadas por el envejecimiento de la pantalla. Esto garantiza una calidad de imagen constante en el tiempo.",
          },
          comfort_safety: {
            "Protección Ocular con Tecnología Flicker Safe":
              "El modo Flicker Safe minimiza el parpadeo de la pantalla, reduciendo el cansancio ocular durante largas horas de trabajo. Esto protege la visión de los profesionales médicos y optimiza su productividad.",
          },
          ergonomics: {
            "Ergonomía y Adaptabilidad":
              'El diseño ergonómico del Monitor de Revisión Clínica IPS LG de 27" y 8 MP permite ajustar la pantalla según las necesidades del usuario:',
            "Características de Ajuste": {
              "Altura ajustable": "Para una postura cómoda",
              "Pivote de dos vías": "Facilitando la orientación vertical u horizontal",
              "Inclinación y rotación": "Adaptándose a diferentes ángulos de visión",
            },
          },
          features: [
            "Panel IPS de 27 pulgadas con resolución 8MP",
            "Diseño en color blanco elegante",
            "Brillo de 350 nits para mayor claridad",
            "Cobertura sRGB del 99% para colores precisos",
            "Ángulo de visión de 178° sin distorsiones",
            "Estabilización automática del brillo",
            "Compatible con DICOM Parte 14",
            "Tecnología Flicker Safe para protección ocular",
            "Altura ajustable para ergonomía",
            "Pivote de dos vías (vertical/horizontal)",
            "Inclinación y rotación ajustables",
            "Sensor integrado de estabilización",
            "Gamma DICOM automático",
          ],
          applications: [
            "Revisión de imágenes clínicas",
            "Radiología y teleradiología",
            "Evaluaciones en consultorios y hospitales",
            "Investigación médica y educación",
          ],
          purchase_info: {
            Financiamiento:
              "Opciones de pago flexibles, incluyendo tarjeta de crédito con financiamiento hasta 12 meses",
            Asesoría:
              "Asesoría especializada para encontrar la mejor solución en tecnología médica",
            Contacto: "Contáctanos para recibir asesoría personalizada y una cotización a medida",
          },
        },
        "21HQ513D": {
          name: 'Monitor de Diagnóstico IPS LG de 21,3" y 3 MP',
          category: "Monitor de Diagnóstico",
          image: require("../assets/images/lg/Monitor-Diagnostico/2-21HQ513D-monitor-diagnostico-21-3mp-1.jpg"),
          description:
            'Calidad y Precisión en Diagnóstico Médico. El Monitor de Diagnóstico IPS LG de 21,3" y 3 MP es una solución profesional diseñada para ofrecer precisión y claridad en la revisión de imágenes médicas. Con tecnología avanzada y un diseño ergonómico, este monitor optimiza los flujos de trabajo en hospitales, clínicas y centros de diagnóstico.',
          pricing: {
            originalPrice: 3009524,
            discountedPrice: 2708571,
            discountPercentage: 10,
            installments: {
              count: 12,
              amount: 225714
            }
          },
          specifications: {
            Tamaño: "21,3 pulgadas",
            Resolución: "2048 x 1536 (3MP)",
            Panel: "IPS",
            Brillo: "400 cd/m²",
            Contraste: "1400:1",
            "Tiempo de Respuesta": "30ms",
            "Ángulos de Visión": "178°/178°",
            Tecnología: "Modo Multiresolución",
            Ergonomía: "Altura ajustable (0-110mm), Inclinación (-5° a 20°), Pivote (+90°)",
            Sensores: "Luminancia automático, Sensor de presencia",
          },
          benefits: {
            "Imágenes Precisas y Claras":
              "La tecnología IPS de 3 MP garantiza una representación de imagen precisa y de alta calidad, permitiendo la evaluación detallada de estudios radiológicos.",
            "Compatibilidad con Dispositivos Médicos":
              "Gracias al modo multiresolución, el monitor se adapta fácilmente a diferentes equipos, asegurando una experiencia de visualización optimizada.",
            "Diagnósticos de Alta Precisión":
              "El Modo de Patología reproduce colores con fidelidad microscópica, facilitando un diagnóstico más exacto para los profesionales de la salud.",
            "Mayor Seguridad y Ahorro Energético":
              "El sensor de presencia apaga la pantalla cuando no detecta movimiento, protegiendo la información del paciente y optimizando el consumo de energía.",
          },
          workflow: {
            "Flujo de Trabajo Eficiente":
              "El monitor cuenta con 5 teclas de acceso rápido para cambiar configuraciones sin interrumpir el trabajo, permitiendo un manejo intuitivo y eficaz.",
            "Reducción de Fatiga Visual":
              "El sensor de luminancia automático ajusta el brillo de la pantalla según la iluminación del entorno, ayudando a reducir la fatiga visual en jornadas prolongadas.",
          },
          ergonomics: {
            "Diseño Ergonómico para Mayor Comodidad":
              "El monitor ofrece múltiples opciones de ajuste para adaptarse a las necesidades del usuario:",
            "Características de Ajuste": {
              "Altura ajustable": "Entre 0 y 110 mm",
              Inclinación: "De -5° a 20° para personalizar la posición de visualización",
              Pivote: "+90° para configuraciones flexibles",
            },
          },
          features: [
            "Panel IPS de 21,3 pulgadas con resolución 3MP",
            "Tecnología IPS para colores precisos",
            "Modo Multiresolución para compatibilidad",
            "Modo de Patología con fidelidad microscópica",
            "Sensor de presencia para seguridad",
            "5 teclas de acceso rápido",
            "Sensor de luminancia automático",
            "Calibración automática DICOM",
            "Altura ajustable (0-110mm)",
            "Inclinación (-5° a 20°)",
            "Pivote (+90°)",
            "Compatibilidad con PACS",
          ],
          applications: [
            "Radiología y teleradiología",
            "Patología digital",
            "Cardiología",
            "Imagenología general",
            "Centros de investigación médica",
          ],
          purchase_info: {
            Financiamiento:
              "Opciones de pago flexibles, incluyendo tarjeta de crédito con financiamiento hasta 12 meses",
            Experiencia: "Experiencia de compra sin complicaciones en RIACS Health Chile",
            Contacto: "Contáctanos para recibir asesoría personalizada y una cotización a medida",
          },
        },
        "21HQ613D": {
          name: 'Monitor de Diagnóstico IPS LG de 21,3" y 5 MP para Mamografía',
          category: "Monitor de Diagnóstico",
          image: require("../assets/images/lg/Monitor-Diagnostico/4-21HQ613D-monitor-diagnostico-21-5mp-1.jpg"),
          description:
            'Precisión y calidad en imágenes mamarias. El Monitor de Diagnóstico IPS LG de 21,3" y 5 MP para Mamografía ha sido diseñado específicamente para la interpretación de imágenes médicas de mama con una precisión excepcional. Gracias a su tecnología avanzada, garantiza la visualización clara y detallada de mamografías digitales, tomosíntesis mamaria y otras modalidades de imagenología.',
          pricing: {
            originalPrice: 7752381,
            discountedPrice: 6600000,
            discountPercentage: 15,
            installments: {
              count: 12,
              amount: 550000
            }
          },
          specifications: {
            Tamaño: "21,3 pulgadas",
            Resolución: "2560 x 2048 (5MP)",
            Panel: "IPS",
            Brillo: "1.100 cd/m²",
            Contraste: "1800:1",
            "Tiempo de Respuesta": "25ms",
            "Ángulos de Visión": "178°/178°",
            Tecnología: "Modo Multiresolución (5/3/2 MP)",
            Calibración: "Sensor de calibración frontal incorporado",
            Ergonomía: "Altura ajustable (110mm), Inclinación (-5° a 20°), Giro (±15°)",
            Iluminación: "Lámpara de pie y luz descendente",
          },
          benefits: {
            "Alta resolución y nitidez en imágenes médicas":
              "Con una resolución de 5 MP y una relación de contraste de 1800:1, este monitor proporciona imágenes nítidas y detalladas. Su brillo de 1.100 cd/m² asegura una visualización clara incluso en condiciones de iluminación variables.",
            "Modo Multiresolución para mayor flexibilidad":
              "El modo multiresolución (5/3/2 MP) permite ajustar la resolución de la pantalla según las necesidades del profesional de la salud, optimizando la compatibilidad con diversas modalidades de imagenología médica.",
            "Diagnósticos más precisos con Modo Patología":
              "El Modo de Patología ofrece una reproducción precisa de colores y texturas, similar a la observación bajo un microscopio. Esto facilita un análisis más detallado de los tejidos mamarios.",
            "Sensor de Calibración Frontal":
              "El sensor de calibración frontal incorporado mantiene la coherencia de la calidad de imagen sin necesidad de equipos externos, garantizando precisión diagnóstica en todo momento.",
          },
          workflow: {
            "Mayor eficiencia con Control Intuitivo":
              "5 teclas de acceso rápido que permiten cambiar de configuración de manera ágil y sencilla.",
            "Sensor de Presencia":
              "Sensor de presencia que apaga automáticamente la pantalla cuando no detecta movimiento, optimizando el consumo energético y protegiendo la privacidad de los pacientes.",
          },
          ergonomics: {
            "Diseño Ergonómico para una Experiencia Cómoda":
              "El monitor ofrece múltiples opciones de ajuste para adaptarse a las necesidades del usuario:",
            "Características de Ajuste": {
              "Altura ajustable": "Hasta 110 mm para personalizar la posición de trabajo",
              Inclinación: "De -5° a 20° para personalizar el ángulo de visión",
              Giro: "±15° para mayor comodidad durante largas jornadas",
              "Iluminación especializada":
                "Lámpara de pie y luz descendente para mejorar la lectura de documentos físicos sin afectar la visualización de la pantalla",
            },
          },
          features: [
            "Panel IPS de 21,3 pulgadas con resolución 5MP",
            "Brillo excepcional de 1.100 cd/m²",
            "Contraste superior de 1800:1",
            "Modo Multiresolución (5/3/2 MP)",
            "Modo de Patología con fidelidad microscópica",
            "Sensor de calibración frontal incorporado",
            "5 teclas de acceso rápido",
            "Sensor de presencia para ahorro energético",
            "Altura ajustable (110mm)",
            "Inclinación (-5° a 20°)",
            "Giro (±15°)",
            "Lámpara de pie integrada",
            "Luz descendente para documentos",
            "Calibración automática DICOM",
            "Compatibilidad con PACS",
          ],
          applications: [
            "Mamografía digital y tomosíntesis mamaria",
            "Radiología y teleradiología",
            "Diagnóstico por imagen de tejidos blandos",
            "Investigación médica y educación",
            "Patología digital",
            "Imagenología especializada",
          ],
          purchase_info: {
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            Experiencia: "Experiencia de compra sin complicaciones en RIACS Health Chile",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades",
          },
        },
        "27HQ713C": {
          name: "27HQ713C",
          category: "Monitor de Revisión Clínica",
          image: require("../assets/images/lg/Monitor-Clinical/8-24HR513C-monitor-clinico-24-2mp-1.jpg"),
          description:
            'Monitor de revisión clínica de 27" con resolución 5MP para aplicaciones médicas avanzadas.',
          specifications: {
            "Tamaño de pantalla": "27 pulgadas",
            Resolución: "2560 x 2048 (5MP)",
            Brillo: "500 cd/m²",
            Contraste: "1500:1",
            "Tiempo de respuesta": "25ms",
            "Ángulos de visión": "178°/178°",
          },
          features: [
            "Resolución 5MP para máximo detalle",
            "Calibración DICOM automática",
            "Panel IPS de grado médico",
            "Sensor de luz ambiental",
          ],
          applications: [
            "Radiología avanzada",
            "Mamografía digital",
            "Tomografía computarizada",
            "Resonancia magnética",
          ],
        },
        // Monitores de Diagnóstico
        "31MU97C": {
          name: "31MU97C",
          category: "Monitor de Diagnóstico",
          image: require("../assets/images/lg/Monitor-Diagnostico/2-21HQ513D-monitor-diagnostico-21-3mp-1.jpg"),
          description:
            'Monitor de diagnóstico de 31" con resolución 8MP para aplicaciones de diagnóstico críticas.',
          specifications: {
            "Tamaño de pantalla": "31 pulgadas",
            Resolución: "4096 x 2160 (8MP)",
            Brillo: "500 cd/m²",
            Contraste: "1700:1",
            "Tiempo de respuesta": "20ms",
            "Ángulos de visión": "178°/178°",
          },
          features: [
            "Resolución 8MP ultra alta",
            "Calibración DICOM Parte 14",
            "Tecnología IPS 4K",
            "Sensor QA integrado",
          ],
          applications: [
            "Radiología de diagnóstico primario",
            "Mamografía de alta resolución",
            "Patología digital",
            "Cirugía guiada por imagen",
          ],
        },
        "32HL512D": {
          name: 'Monitor de Diagnóstico LG de 31,5" y 8 MP',
          category: "Monitor de Diagnóstico",
          image: require("../assets/images/lg/Monitor-Diagnostico/1-32HL512D-monitor-diagnostico-31-8mp-2.jpg"),
          description:
            "Precisión y calidad en diagnóstico médico. El Monitor de Diagnóstico LG de 8 MP es una solución avanzada diseñada para mejorar la precisión en la interpretación de imágenes médicas en hospitales, clínicas y centros de teleradiología. Con tecnología de última generación y una pantalla IPS de 31,5 pulgadas, proporciona una calidad de imagen superior y una ergonomía optimizada para largas jornadas de trabajo. Además, su resolución múltiple facilita su adaptabilidad a diversas modalidades de imagen.",
          pricing: {
            originalPrice: 2942857,
            discountedPrice: 2648571,
            discountPercentage: 10,
            installments: {
              count: 12,
              amount: 220714
            }
          },
          specifications: {
            Tamaño: "31,5 pulgadas",
            Resolución: "4096 x 2160 (8MP)",
            Panel: "IPS",
            Brillo: "500 cd/m²",
            Contraste: "1700:1",
            "Tiempo de Respuesta": "20ms",
            "Ángulos de Visión": "178°/178°",
            Tecnología: "Modo Multiresolución (8/6/4 MP)",
            Calibración: "DICOM Parte 14",
            Ergonomía: "Altura ajustable (0-110mm), Inclinación (-5° a 20°), Pivote bidireccional",
            Diseño: "4 lados sin bordes para configuraciones duales",
          },
          benefits: {
            "Imagen precisa desde cualquier ángulo":
              "Gracias a su panel IPS de 8 MP, este monitor asegura una visualización uniforme y sin distorsiones desde cualquier ángulo. Su tecnología avanzada permite evaluar imágenes con la mayor fidelidad, optimizando la precisión en el diagnóstico médico.",
            "Compatibilidad con todos los dispositivos":
              "El modo multiresolución (8/6/4 MP) facilita la conexión con diversos dispositivos médicos, permitiendo a los profesionales ajustar la resolución para optimizar la visualización según las necesidades del estudio.",
            "Reproducción de colores fidedigna en Modo Patología":
              "El modo de patología ofrece una reproducción de colores realista, similar a la observada bajo un microscopio, mejorando la interpretación de biopsias y otros estudios histológicos.",
            "Estabilización del brillo":
              "Para garantizar una imagen estable y precisa a lo largo del tiempo, el monitor incluye un sensor que ajusta automáticamente la estabilidad del brillo, compensando las variaciones provocadas por el envejecimiento de la pantalla.",
          },
          workflow: {
            "Visualización múltiple con PBP y controlador dual":
              "Con Picture By Picture (PBP) y controlador dual, el monitor puede manejar varias fuentes de imagen simultáneamente, permitiendo a los profesionales comparar estudios o acceder a información de distintos dispositivos sin interrupciones.",
          },
          ergonomics: {
            "Diseño ergonómico y optimizado para largas jornadas":
              "El diseño ergonómico del Monitor de Diagnóstico LG de 8 MP mejora la comodidad y la eficiencia del profesional de la salud. Su ajuste en altura, inclinación y pivote bidireccional permite personalizar el espacio de trabajo para una experiencia más cómoda y productiva.",
            "Opciones de ajuste": {
              Pivote: "De dos vías para máxima flexibilidad",
              Altura: "Regulable entre 0 y 110 mm",
              Inclinación: "De -5° a 20° para personalizar el ángulo de visión",
              "Diseño sin bordes":
                "4 lados sin bordes, ideal para configuraciones de doble pantalla en posición vertical",
            },
          },
          features: [
            "Panel IPS de 31,5 pulgadas con resolución 8MP",
            "Tecnología IPS 4K ultra alta definición",
            "Modo Multiresolución (8/6/4 MP)",
            "Calibración DICOM Parte 14",
            "Modo de Patología con reproducción microscópica",
            "Sensor de estabilización de brillo automático",
            "Picture By Picture (PBP) con controlador dual",
            "Altura ajustable (0-110mm)",
            "Inclinación (-5° a 20°)",
            "Pivote bidireccional",
            "Diseño de 4 lados sin bordes",
            "Sensor QA integrado",
            "Compatibilidad con PACS",
            "Visualización uniforme desde cualquier ángulo",
            "Tecnología avanzada de compensación de envejecimiento",
          ],
          applications: [
            "Radiología y teleradiología",
            "Patología digital",
            "Cardiología",
            "Imagenología general",
            "Salas de lectura diagnóstica",
            "Centros de investigación médica",
            "Diagnóstico de alta precisión",
            "Interpretación de biopsias",
          ],
          purchase_info: {
            Financiamiento:
              "Opciones de pago flexibles, incluyendo tarjeta de crédito con financiamiento de hasta 12 meses",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades",
          },
        },
        // Monitores Quirúrgicos
        "27HN711S": {
          name: "27HN711S",
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Clinical/8-24HR513C-monitor-clinico-24-2mp-1.jpg"),
          description:
            'Monitor quirúrgico de 27" 4K diseñado específicamente para entornos de quirófano.',
          specifications: {
            "Tamaño de pantalla": "27 pulgadas",
            Resolución: "3840 x 2160 (4K UHD)",
            Brillo: "350 cd/m²",
            Contraste: "1000:1",
            "Tiempo de respuesta": "5ms",
            Protección: "IPX6",
          },
          features: [
            "Diseño resistente a líquidos IPX6",
            "Superficie antimicrobiana",
            "Control remoto inalámbrico",
            "Múltiples entradas de video",
          ],
          applications: [
            "Cirugía laparoscópica",
            "Cirugía endoscópica",
            "Procedimientos mínimamente invasivos",
            "Cirugía robótica",
          ],
        },
        "27HQ710S": {
          name: "Monitor quirúrgico 4K de 27 pulgadas con mini-LED",
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Quirurjico/13-27HQ710S-monitor-quirurgico-4k-27-1.jpg"),
          description:
            "Precisión y Tecnología en Cirugía. El Monitor quirúrgico 4K de 27 pulgadas con mini-LED de LG está diseñado para proporcionar imágenes ultra precisas y alta fidelidad cromática, esenciales en entornos quirúrgicos exigentes. Con tecnología avanzada Mini-LED, HDR10 y sRGB 139 %, este monitor optimiza la visualización para procedimientos quirúrgicos de alta precisión.",
          specifications: {
            Tamaño: "27 pulgadas",
            Resolución: "3840 x 2160 (4K UHD)",
            Panel: "Mini-LED",
            Tecnología: "HDR10",
            "Cobertura de Color": "sRGB 139% (área) y DCI-P3 98% (CIE1976)",
            "Cristal Protector": "1,3 t antirreflejos y antihuellas",
            Resistencia: "Agua y polvo",
            Conectividad: "Hasta 4PBP y PIP/3G-SDI",
            Conmutación: "Conmutador de entrada por error",
            "Funciones Especiales": "Modo espejo y rotación 180°",
          },
          benefits: {
            "Alta Calidad de Imagen con Mini-LED":
              "Gracias a la tecnología Mini-LED, este monitor quirúrgico ofrece un contraste superior, con negros más profundos y áreas brillantes más nítidas. Esto permite visualizar imágenes quirúrgicas con mayor precisión, profundidad y definición.",
            "Tecnología Mini-LED para una Visualización Óptima":
              "La tecnología Mini-LED proporciona un control preciso de la iluminación, mejorando significativamente el contraste y la calidad de imagen en procedimientos quirúrgicos críticos.",
            "Amplia Gama de Colores":
              "La amplia gama de colores sRGB 139 % y DCI-P3 98 % (CIE1976) asegura una representación precisa de los colores, indispensable para la visualización detallada de tejidos y estructuras anatómicas durante procedimientos quirúrgicos.",
          },
          workflow: {
            "Funcionalidad Avanzada en el Quirófano":
              "Diseñado para optimizar el flujo de trabajo quirúrgico con características especializadas.",
            "Modo Espejo y Rotación":
              "Este monitor permite ajustar la orientación de la imagen según las necesidades del equipo quirúrgico, proporcionando una visualización optimizada con funciones de rotación 180° y modo espejo.",
            "Admite Hasta 4PBP y PIP/3G-SDI":
              "El Monitor quirúrgico 4K de 27 pulgadas con mini-LED permite visualizar hasta 4 fuentes de video simultáneamente gracias a su tecnología Picture by Picture (PBP) y Picture in Picture (PIP), facilitando la supervisión de múltiples imágenes médicas en tiempo real.",
            "Conmutador de Entrada de Conmutación por Error":
              "Este monitor cuenta con una función de conmutación por error, lo que permite cambiar automáticamente a una fuente alternativa en caso de interrupción de la señal, garantizando continuidad operativa en el quirófano.",
          },
          ergonomics: {
            "Diseño Duradero y Seguro":
              "Construido para resistir las demandas del entorno quirúrgico con características de protección avanzadas.",
            "Cristal Protector Antirreflejos y Antihuellas":
              "La pantalla está equipada con un cristal protector de 1,3 t que reduce reflejos y evita la acumulación de huellas dactilares, garantizando una visualización nítida en cualquier condición lumínica.",
            "Resistente al Agua y al Polvo":
              "Diseñado para su uso en quirófanos, este monitor cuenta con protección contra líquidos y partículas, asegurando su durabilidad y facilitando su limpieza y desinfección.",
            "Conveniencia para el Usuario":
              "El Monitor quirúrgico 4K de 27 pulgadas con mini-LED no solo ofrece una calidad de imagen excepcional, sino que también está diseñado para brindar comodidad y eficiencia en entornos quirúrgicos. Su interfaz intuitiva y opciones de conectividad avanzada permiten una integración fluida con distintos equipos médicos.",
          },
          features: [
            "Tecnología Mini-LED avanzada",
            "Resolución 4K UHD (3840 x 2160)",
            "HDR10 para mayor rango dinámico",
            "sRGB 139% y DCI-P3 98% de cobertura de color",
            "Cristal protector antirreflejos de 1,3 t",
            "Superficie antihuellas",
            "Resistencia al agua y polvo",
            "Soporte para hasta 4PBP y PIP",
            "Conectividad 3G-SDI",
            "Modo espejo y rotación 180°",
            "Conmutador de entrada por error",
            "Interfaz intuitiva",
            "Opciones de conectividad avanzada",
            "Integración fluida con equipos médicos",
            "Diseño optimizado para quirófanos",
          ],
          applications: [
            "Cirugía de alta precisión",
            "Procedimientos laparoscópicos",
            "Cirugía endoscópica",
            "Cirugía robótica",
            "Procedimientos mínimamente invasivos",
            "Cirugía cardiovascular",
            "Neurocirugía",
            "Cirugía ortopédica",
            "Visualización de múltiples fuentes médicas",
            "Quirófanos modernos",
          ],
          purchase_info: {
            "Adquiere la Mejor Tecnología para Cirugía con RIACS Health":
              "En RIACS Health, ofrecemos asesoría personalizada, soporte técnico 24/7 y opciones de financiamiento para garantizar que nuestros clientes cuenten con la mejor tecnología en el sector salud.",
            "Optimización Quirúrgica":
              "Optimiza la precisión quirúrgica con la tecnología líder de LG y la garantía de RIACS Health.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades quirúrgicas",
          },
        },
        "32HL710S": {
          name: "Monitor quirúrgico IPS 4K de 31,5 pulgadas de LG",
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Quirurjico/12-32HL710S-monitor-quirurgico-4k-31-2.jpg"),
          description:
            "Precisión y Tecnología para el Quirófano. El Monitor quirúrgico IPS 4K de 31,5» de LG está diseñado para proporcionar imágenes de alta resolución con una fidelidad cromática excepcional, garantizando una visualización óptima en procedimientos quirúrgicos. Gracias a su tecnología avanzada, este monitor es una herramienta indispensable en entornos médicos de alta exigencia.",
          specifications: {
            Tamaño: "31,5 pulgadas",
            Resolución: "3840 x 2160 (4K UHD)",
            Panel: "IPS",
            "Cobertura de Color": "sRGB 115% (área), más del 99% (cobertura)",
            "Cumplimiento DICOM": "Más del 99% DICOM Parte 14",
            "Estabilización de Brillo": "Sensor de brillo inteligente",
            "Soporte HDR": "HDR10",
            Conectividad: "Hasta 4 señales simultáneas (PBP, PIP, Multi-Entrada)",
            "Funciones Especiales": "Espejo y rotación 180°",
            "Protección Frontal": "IP35",
            "Protección Cuerpo": "IP32",
            "Vidrio Protector": "Incluido para protección adicional",
          },
          benefits: {
            "Tamaño y Resolución para Precisión Quirúrgica":
              "El Monitor quirúrgico IPS 4K de 31,5» de LG está diseñado para proporcionar imágenes nítidas y detalladas con una resolución 4K UHD (3840 x 2160 píxeles). Su amplia pantalla permite a los cirujanos y al equipo médico visualizar con claridad imágenes de alta precisión desde diferentes ángulos.",
            "Fidelidad Cromática Excepcional":
              "El modelo 32HL710S ofrece una cobertura de color sRGB del 115 % y más del 99 % de cobertura DICOM Parte 14, optimizando el reconocimiento preciso del color y la percepción de profundidad, aspectos cruciales para intervenciones quirúrgicas.",
            "Estabilización del Brillo":
              "Este monitor quirúrgico de LG incorpora un sensor de brillo inteligente que ajusta la intensidad de la luz de fondo y optimiza la imagen según las condiciones de iluminación del quirófano. Esto garantiza una visualización estable y sin fluctuaciones durante los procedimientos.",
            "Soporte para HDR10":
              "El HDR10 permite la reproducción de imágenes con un rango dinámico extendido, asegurando una mayor profundidad y detalle en áreas brillantes y oscuras, ideal para cámaras endoscópicas y otros dispositivos médicos compatibles.",
          },
          workflow: {
            "Funciones Avanzadas para una Visualización Óptima":
              "Diseñado para optimizar el flujo de trabajo quirúrgico con características especializadas para entornos médicos exigentes.",
            "Varias Señales en una Pantalla (PBP, PIP y Multi-Entrada)":
              "El modelo 32HL710S admite hasta 4 señales simultáneas con opciones de PBP (Picture by Picture) y PIP (Picture in Picture), permitiendo a los cirujanos visualizar imágenes de distintos dispositivos médicos en una sola pantalla, como cámaras endoscópicas, imágenes de signos vitales y fluoroscopía.",
            "Funciones de Espejo y Rotación":
              "Las funciones de espejo y rotación permiten adaptar la visualización de la pantalla según las necesidades del procedimiento. Con una rotación de 180 grados, el equipo médico puede ajustar la imagen en tiempo real para una mejor comodidad operativa.",
          },
          ergonomics: {
            "Seguridad y Durabilidad en Entornos Médicos":
              "Construido para resistir las demandas del entorno quirúrgico con características de protección avanzadas.",
            "Resistente al Agua y al Polvo":
              "El Monitor quirúrgico IPS 4K de 31,5» de LG cuenta con una certificación de protección IP35 en el frontal y IP32 en el cuerpo, garantizando resistencia al agua y polvo. Esto permite su uso seguro en entornos quirúrgicos expuestos a fluidos y facilita su limpieza y desinfección.",
            "Vidrio Protector":
              "Diseñado para soportar las condiciones del quirófano, este monitor incluye un vidrio protector que brinda una capa adicional de seguridad contra salpicaduras y facilita la limpieza sin comprometer la calidad de la imagen.",
          },
          features: [
            "Panel IPS de 31,5 pulgadas con resolución 4K UHD",
            "Cobertura de color sRGB 115% (área)",
            "Más del 99% de cobertura DICOM Parte 14",
            "Sensor de brillo inteligente",
            "Soporte HDR10 para rango dinámico extendido",
            "Hasta 4 señales simultáneas (PBP, PIP)",
            "Funciones de espejo y rotación 180°",
            "Protección IP35 frontal e IP32 cuerpo",
            "Vidrio protector incluido",
            "Resistencia al agua y polvo",
            "Optimizado para cámaras endoscópicas",
            "Compatible con dispositivos médicos",
            "Visualización estable sin fluctuaciones",
            "Fácil limpieza y desinfección",
            "Diseño especializado para quirófanos",
          ],
          applications: [
            "Cirugía endoscópica",
            "Procedimientos laparoscópicos",
            "Cirugía de alta precisión",
            "Visualización de signos vitales",
            "Fluoroscopía quirúrgica",
            "Cirugía cardiovascular",
            "Neurocirugía",
            "Cirugía ortopédica",
            "Procedimientos mínimamente invasivos",
            "Quirófanos modernos",
          ],
          purchase_info: {
            "Tecnología Avanzada para Quirófanos":
              "Adquiere la mejor tecnología quirúrgica con la garantía y soporte de RIACS Health Chile, distribuidores oficiales de LG Medical Display.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",

            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades quirúrgicas",
          },
        },
        "32HN711S": {
          name: "32HN711S",
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Diagnostico/2-21HQ513D-monitor-diagnostico-21-3mp-1.jpg"),
          description:
            'Monitor quirúrgico de 32" 4K con características avanzadas para quirófanos modernos.',
          specifications: {
            "Tamaño de pantalla": "32 pulgadas",
            Resolución: "3840 x 2160 (4K UHD)",
            Brillo: "400 cd/m²",
            Contraste: "1200:1",
            "Tiempo de respuesta": "5ms",
            Protección: "IPX6",
          },
          features: [
            "Pantalla grande 4K para mejor visualización",
            "Recubrimiento antimicrobiano",
            "Control táctil en panel frontal",
            "Compatibilidad con sistemas quirúrgicos",
          ],
          applications: [
            "Cirugía cardiovascular",
            "Neurocirugía",
            "Cirugía ortopédica",
            "Procedimientos complejos",
          ],
        },
        "32HQ713D": {
          name: "Monitor de Diagnóstico LG IPS de 31,5 pulgadas y 8 MP en color negro",
          category: "Monitor de Diagnóstico",
          image: require("../assets/images/lg/Monitor-Diagnostico/3-32HQ713D-monitor-diagnostico-31-8mp-2.jpg"),
          description:
            "Precisión y Calidad para Diagnósticos Médicos. El Monitor de Diagnóstico LG IPS de 31,5 pulgadas y 8 MP en color negro es una solución de vanguardia diseñada para garantizar una visualización precisa y confiable en entornos médicos. Gracias a su panel IPS de alta resolución, brinda una claridad excepcional en la interpretación de imágenes médicas, optimizando la eficiencia de los profesionales de la salud.",
          pricing: {
            originalPrice: 6533333,
            discountedPrice: 5562162,
            discountPercentage: 15,
            installments: {
              count: 12,
              amount: 463514
            }
          },
          specifications: {
            Tamaño: "31,5 pulgadas",
            Resolución: "4096 x 2160 (8MP)",
            Panel: "IPS Black",
            Brillo: "500 cd/m²",
            Contraste: "2000:1",
            "Tiempo de Respuesta": "20ms",
            "Ángulos de Visión": "178°/178°",
            Tecnología: "Modo Multiresolución (8/6/4 MP)",
            Calibración: "DICOM Parte 14",
            Color: "Negro",
            Ergonomía: "Altura ajustable (0-110mm), Inclinación (-5° a 15°), Rotación (±15°)",
            Iluminación: "Lámpara de pie ergonómica y luz descendente",
          },
          benefits: {
            "Imágenes de Alta Definición para Evaluaciones Médicas":
              "Su panel IPS Black ofrece niveles de negro más profundos y una relación de contraste de 2000:1, permitiendo detectar detalles sutiles en imágenes radiológicas y de otras especialidades.",
            "Ajuste Personalizado con Modo Multiresolución":
              "Gracias al modo multiresolución (8/6/4 MP), el monitor se adapta a las necesidades de distintos dispositivos médicos, proporcionando flexibilidad en la interpretación de imágenes.",
            "Precisión Diagnóstica con Modo de Patología":
              "El Modo de Patología optimiza la representación de colores y texturas, brindando un nivel de detalle comparable al de un microscopio, ideal para exámenes histológicos y estudios avanzados.",
          },
          workflow: {
            "Sensor de presencia":
              "Optimiza el consumo de energía y protege la privacidad de los pacientes",
            "6 teclas de acceso rápido":
              "Permiten cambiar configuraciones sin interrupciones, mejorando la productividad",
            "Vista de enfoque":
              "Facilita la observación de áreas específicas, atenuando el resto de la imagen",
          },
          ergonomics: {
            "Diseño Ergonómico para Mayor Comodidad":
              "El monitor está diseñado para proporcionar máxima comodidad durante largas jornadas de trabajo médico.",
            "Opciones de ajuste": {
              Altura: "Ajustable entre 0 y 110 mm",
              Inclinación: "De -5° a 15° para una experiencia de visualización personalizada",
              Rotación: "±15° para mayor flexibilidad en el uso",
              "Iluminación especializada":
                "Lámpara de pie ergonómica y luz descendente para optimizar la lectura de documentos físicos",
            },
          },
          features: [
            "Panel IPS Black de 31,5 pulgadas con resolución 8MP",
            "Tecnología IPS Black con contraste 2000:1",
            "Modo Multiresolución (8/6/4 MP)",
            "Calibración DICOM Parte 14",
            "Modo de Patología con fidelidad microscópica",
            "Sensor de presencia para seguridad y eficiencia",
            "6 teclas de acceso rápido",
            "Vista de enfoque para análisis detallado",
            "Altura ajustable (0-110mm)",
            "Inclinación (-5° a 15°)",
            "Rotación (±15°)",
            "Lámpara de pie ergonómica",
            "Luz descendente para documentos",
            "Diseño en color negro elegante",
            "Compatibilidad con PACS",
          ],
          applications: [
            "Radiología y teleradiología",
            "Patología digital",
            "Imagenología general",
            "Cardiología",
            "Investigación médica y educación",
            "Diagnóstico de alta precisión",
            "Interpretación de estudios histológicos",
            "Salas de lectura diagnóstica",
          ],
          purchase_info: {
            "Experiencia de Compra Fácil y Rápida":
              "En RIACS Health Chile, ofrecemos un proceso de compra sin complicaciones, con opciones de financiamiento hasta 12 meses con tarjeta de crédito. Adquiere tecnología médica avanzada con la confianza de un distribuidor especializado.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades",
          },
        },
        "31HN713D": {
          name: "Monitor de Diagnóstico para Mamografía de 31 pulgadas y 12 MP",
          category: "Monitor de Diagnóstico",
          image: require("../assets/images/lg/Monitor-Diagnostico/5-31HN713D-monitor-diagnostico-31-12mp-2.jpg"),
          description:
            "Precisión y claridad en imágenes mamarias. El Monitor de Diagnóstico para Mamografía de 31 pulgadas y 12 MP está diseñado para ofrecer imágenes nítidas y de alta precisión, facilitando la interpretación de mamografías digitales y tomosíntesis mamaria. Su tecnología avanzada y su diseño ergonómico lo convierten en una herramienta imprescindible en entornos de diagnóstico médico.",
          pricing: {
            originalPrice: 17575238,
            discountedPrice: 14962703,
            discountPercentage: 15,
            installments: {
              count: 12,
              amount: 1246892
            }
          },
          specifications: {
            Tamaño: "31 pulgadas",
            Resolución: "4096 x 3072 (12MP)",
            Panel: "IPS",
            Brillo: "1200 cd/m²",
            Contraste: "1700:1",
            "Tiempo de Respuesta": "20ms",
            "Ángulos de Visión": "178°/178°",
            Tecnología: "Modo Multiresolución (12/6 MP)",
            Calibración: "DICOM Parte 14",
            Autocalibración: "Sensor frontal integrado",
            Ergonomía: "Altura ajustable (0-110mm), Inclinación (-5° a 15°), Rotación (±15°)",
            Iluminación: "Lámpara de pie y luz descendente",
          },
          benefits: {
            "Imágenes de Alta Definición para un Diagnóstico Preciso":
              "Con su resolución de 12 MP y una relación de contraste optimizada, este monitor proporciona imágenes claras y detalladas. Su brillo de 1200 cd/m² garantiza una visualización excepcional en cualquier condición de iluminación.",
            "Modo Multiresolución para una Mayor Compatibilidad":
              "El modo multiresolución (12/6 MP) permite ajustar la pantalla según los requisitos del estudio, asegurando compatibilidad con diversas modalidades de imagenología médica.",
            "Diagnósticos Más Precisos con Modo de Patología":
              "El Modo de Patología proporciona una reproducción precisa de colores y detalles, permitiendo a los profesionales observar tejidos con una calidad equiparable a la visión microscópica.",
            "Autocalibración para Imágenes Confiables":
              "El sensor de autocalibración frontal mantiene la calidad de imagen sin necesidad de equipos externos, asegurando estabilidad en la interpretación de estudios a lo largo del tiempo.",
          },
          workflow: {
            "Mayor Eficiencia con Sensor de Presencia y Control Intuitivo":
              "Optimiza el flujo de trabajo médico con tecnología avanzada de detección y control.",
            "Sensor de presencia":
              "Apaga automáticamente la pantalla cuando no detecta movimiento, optimizando el consumo energético y protegiendo la privacidad de los pacientes",
            "6 teclas de acceso rápido":
              "Facilitan la configuración del monitor sin interrumpir el flujo de trabajo",
          },
          ergonomics: {
            "Diseño Ergonómico para una Experiencia Cómoda":
              "El monitor está diseñado para proporcionar máxima comodidad durante largas jornadas de diagnóstico mamográfico.",
            "Opciones de ajuste": {
              Altura: "Ajustable hasta 110 mm",
              Inclinación: "De -5° a 15° para personalizar el ángulo de visión",
              Rotación: "±15° para mayor flexibilidad",
              "Iluminación especializada":
                "Lámpara de pie y luz descendente para mejorar la lectura de documentos físicos sin afectar la visibilidad de la pantalla",
            },
          },
          features: [
            "Panel IPS de 31 pulgadas con resolución 12MP",
            "Tecnología IPS especializada para mamografía",
            "Modo Multiresolución (12/6 MP)",
            "Calibración DICOM Parte 14",
            "Modo de Patología con fidelidad microscópica",
            "Sensor de autocalibración frontal",
            "Sensor de presencia para eficiencia energética",
            "6 teclas de acceso rápido",
            "Brillo optimizado de 1200 cd/m²",
            "Altura ajustable (0-110mm)",
            "Inclinación (-5° a 15°)",
            "Rotación (±15°)",
            "Lámpara de pie ergonómica",
            "Luz descendente para documentos",
            "Compatibilidad con PACS",
          ],
          applications: [
            "Mamografía digital y tomosíntesis mamaria",
            "Radiología y teleradiología",
            "Diagnóstico por imagen de tejidos blandos",
            "Investigación médica y educación",
            "Diagnóstico de alta precisión",
            "Interpretación de estudios mamográficos",
            "Salas de lectura especializada",
            "Centros de detección temprana",
          ],
          purchase_info: {
            "Experiencia de Compra Fácil y Rápida":
              "En RIACS Health Chile, ofrecemos un proceso de compra sin complicaciones, con opciones de financiamiento hasta 12 meses con tarjeta de crédito. Adquiere tecnología médica avanzada con la confianza de un distribuidor especializado.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades",
          },
        },
        "32HL714S": {
          name: 'Monitor Quirúrgico IPS 4K de 31,5" de LG',
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Quirurjico/11-32HL714S-monitor-quirurgico-4k-31-1.jpg"),
          description:
            'Precisión y calidad en entornos quirúrgicos. El Monitor Quirúrgico IPS 4K de 31,5" de LG es una herramienta imprescindible para la visualización de imágenes médicas en alta resolución durante procedimientos quirúrgicos. Su tecnología IPS y resolución 4K garantizan detalles nítidos, colores precisos y una visualización sin distorsiones desde cualquier ángulo.',
          specifications: {
            Tamaño: "31,5 pulgadas",
            Resolución: "3840 x 2160 (4K UHD)",
            Panel: "IPS",
            "Cobertura de Color": "sRGB 115% (área), más del 99% (cobertura)",
            "Cumplimiento DICOM": "Más del 99% DICOM Parte 14",
            "Transmisión de Video": "12G-SDI para transmisión 4K sin latencia",
            "Soporte HDR": "HDR10",
            Conectividad: "Hasta 4 señales simultáneas (PBP, PIP, Multi-Entrada)",
            "Funciones Especiales": "Espejo y rotación 180°",
            "Protección Frontal": "IP35",
            "Protección Cuerpo": "IP32",
            "Conmutador de Entrada": "Conmutación por error automática",
          },
          benefits: {
            "Visualización de Alta Definición para Cirugías Precisas":
              "La combinación de resolución 4K, tecnología IPS y HDR10 permite visualizar cada detalle con total claridad, optimizando la precisión en procedimientos quirúrgicos.",
            "Reproducción de Colores Realista":
              "El sRGB 115% (área) y más del 99% (cobertura) garantiza una percepción precisa del color, fundamental para cirugías invasivas donde cada detalle es clave.",
            "Transmisión 4K en Tiempo Real con 12G-SDI":
              "Gracias a la compatibilidad con 12G-SDI, este monitor permite la transmisión de video 4K sin latencia mediante un único cable coaxial, asegurando una cirugía estable y sin interrupciones.",
          },
          workflow: {
            "Funciones Avanzadas para un Flujo de Trabajo Eficiente":
              "Optimiza el flujo de trabajo quirúrgico con características especializadas para entornos médicos exigentes.",
            "Visualización de Múltiples Señales con PBP y PIP":
              "El modo PBP (Picture by Picture) y PIP (Picture in Picture) permiten la visualización simultánea de hasta 4 señales de video, facilitando la integración de diversas fuentes médicas en una misma pantalla.",
            "Conmutador de Entrada de Conmutación por Error":
              "Si la señal principal falla, el monitor cambia automáticamente a una fuente de respaldo, garantizando la continuidad de la visualización en todo momento.",
            "Modo Espejo y Rotación para Mayor Adaptabilidad":
              "La función de espejo y rotación permite ajustar la orientación de la imagen para optimizar la visualización desde diferentes ángulos dentro del quirófano.",
          },
          ergonomics: {
            "Protección y Durabilidad en el Entorno Quirúrgico":
              "Construido para resistir las demandas del entorno quirúrgico con características de protección avanzadas.",
            "Resistente al Agua y al Polvo con Certificación IP35/IP32":
              "IP35 (frontal): Protección contra el contacto con fluidos y partículas sólidas durante cirugías. IP32 (cuerpo): Resistencia a gotas de agua y pequeñas partículas, asegurando durabilidad en el quirófano.",
          },
          features: [
            "Panel IPS de 31,5 pulgadas con resolución 4K UHD",
            "Tecnología IPS para visualización sin distorsiones",
            "Cobertura de color sRGB 115% y DICOM 99%",
            "Transmisión 4K sin latencia con 12G-SDI",
            "Soporte HDR10 para rango dinámico extendido",
            "Visualización simultánea de hasta 4 señales (PBP/PIP)",
            "Conmutador automático de entrada por error",
            "Funciones de espejo y rotación 180°",
            "Certificación IP35 frontal e IP32 cuerpo",
            "Resistencia al agua y polvo",
            "Diseño optimizado para quirófano",
            "Compatibilidad con múltiples dispositivos médicos",
            "Visualización estable sin interrupciones",
            "Facilidad de limpieza y desinfección",
            "Adaptabilidad a diferentes ángulos de visión",
          ],
          applications: [
            "Cirugías mínimamente invasivas",
            "Procedimientos endoscópicos y laparoscópicos",
            "Salas de operaciones avanzadas",
            "Visualización de imágenes médicas en tiempo real",
            "Cirugía robótica",
            "Procedimientos cardiovasculares",
            "Neurocirugía",
            "Cirugía ortopédica",
            "Visualización de múltiples fuentes médicas",
            "Quirófanos modernos de alta tecnología",
          ],
          purchase_info: {
            "Experiencia de Compra Fácil y Rápida":
              "Ofrecemos opciones de financiamiento hasta 12 meses con tarjeta de crédito. Nuestro equipo de especialistas en tecnología médica está listo para asesorarte y brindarte la mejor solución según tus necesidades.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades quirúrgicas",
          },
        },
        "55MH5K": {
          name: 'Monitor Quirúrgico IPS 4K de 55" de LG',
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Quirurjico/9-55MH5K-monitor-quirurgico-4k-55-2.jpg"),
          description:
            'Precisión y calidad para entornos quirúrgicos. El Monitor Quirúrgico IPS 4K de 55" de LG ofrece una solución avanzada para visualizar imágenes en tiempo real durante procedimientos quirúrgicos. Gracias a su resolución ultra HD y su compatibilidad con HDR10, proporciona una claridad de imagen superior, asegurando precisión en cada intervención.',
          specifications: {
            Tamaño: "55 pulgadas",
            Resolución: "3840 x 2160 (4K UHD)",
            Panel: "IPS",
            "Ángulo de Visión": "178° horizontal y vertical",
            "Soporte HDR": "HDR10 para mejor contraste",
            "Transmisión de Video": "12G-SDI para transmisión 4K sin latencia",
            Conectividad: "Hasta 4 señales simultáneas (PBP, PIP)",
            "Funciones Especiales": "Espejo y rotación",
            "Protección Frontal": "IP45",
            "Protección Cuerpo": "IP32",
            "Conmutador de Entrada": "Conmutación por error automática",
          },
          benefits: {
            "Visualización de Alta Definición para Procedimientos Precisos":
              "Con una resolución 4K y panel IPS, este monitor proporciona imágenes detalladas con colores realistas. Su amplio ángulo de visión de 178° permite a los equipos médicos observar con claridad desde distintos ángulos sin distorsiones.",
            "Compatibilidad con HDR10 para Mejor Contraste":
              "La tecnología HDR10 optimiza la representación de imágenes, mejorando los detalles en áreas oscuras y brillantes, lo que facilita la interpretación precisa durante las cirugías.",
            "Transmisión 4K en Tiempo Real con 12G-SDI":
              "La conectividad 12G-SDI permite la transmisión de señales 4K sin latencia a largas distancias mediante un solo cable coaxial, asegurando imágenes en tiempo real sin interrupciones.",
          },
          workflow: {
            "Funciones Avanzadas para Mayor Seguridad":
              "Optimiza el flujo de trabajo quirúrgico con características especializadas para entornos médicos exigentes.",
            "Visualización de Múltiples Señales con PBP y PIP":
              "Este monitor soporta hasta 4 señales simultáneamente gracias a sus modos Picture by Picture (PBP) y Picture in Picture (PIP), permitiendo una supervisión integral de diversas fuentes en una misma pantalla.",
            "Conmutador de Entrada de Conmutación por Error":
              "Si la señal principal falla, el monitor cambiará automáticamente a una fuente de respaldo, asegurando una visualización continua sin interrupciones. Una vez restaurada la señal principal, el monitor regresará automáticamente a ella.",
            "Modo Espejo y Rotación para Mayor Flexibilidad":
              "El modo espejo y rotación permite adaptar la imagen en función de las necesidades del equipo quirúrgico, facilitando su visualización desde diferentes posiciones.",
          },
          ergonomics: {
            "Protección y Durabilidad en el Entorno Quirúrgico":
              "Construido para resistir las demandas del entorno quirúrgico con características de protección avanzadas.",
            "Resistencia a Líquidos y Sólidos con Clasificación IP45/IP32":
              "IP45 (frontal): Protegido contra chorros de agua a baja presión y partículas sólidas mayores de 1 mm. IP32 (cuerpo): Resistencia a gotas de agua y objetos sólidos de más de 2.5 mm. Esto garantiza un funcionamiento confiable incluso en condiciones desafiantes dentro del quirófano.",
          },
          features: [
            "Panel IPS de 55 pulgadas con resolución 4K UHD",
            "Ángulo de visión amplio de 178° horizontal y vertical",
            "Tecnología HDR10 para mejor contraste",
            "Transmisión 4K sin latencia con 12G-SDI",
            "Visualización simultánea de hasta 4 señales (PBP/PIP)",
            "Conmutador automático de entrada por error",
            "Funciones de espejo y rotación",
            "Certificación IP45 frontal e IP32 cuerpo",
            "Resistencia a chorros de agua a baja presión",
            "Protección contra partículas sólidas",
            "Diseño optimizado para quirófano de gran tamaño",
            "Compatibilidad con múltiples dispositivos médicos",
            "Visualización estable sin interrupciones",
            "Facilidad de limpieza y desinfección",
            "Adaptabilidad a diferentes posiciones de visualización",
          ],
          applications: [
            "Cirugías mínimamente invasivas",
            "Endoscopias y laparoscopias",
            "Salas de operaciones avanzadas",
            "Visualización de imágenes médicas en tiempo real",
            "Cirugía robótica",
            "Procedimientos cardiovasculares",
            "Neurocirugía",
            "Cirugía ortopédica",
            "Visualización de múltiples fuentes médicas",
            "Quirófanos modernos de gran escala",
          ],
          purchase_info: {
            "Experiencia de Compra Fácil y Rápida":
              "En RIACS Health Chile, ofrecemos un proceso de compra sin complicaciones, con opciones de financiamiento hasta 12 meses con tarjeta de crédito. Nuestro equipo de especialistas en tecnología médica está listo para asesorarte y brindarte la mejor solución según tus necesidades.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades quirúrgicas",
          },
        },
        "27HK510S": {
          name: "Monitor quirúrgico IPS Full HD de 27» de LG",
          category: "Monitor Quirúrgico",
          image: require("../assets/images/lg/Monitor-Quirurjico/14-27HK510S-monitor-quirurgico-fullhd-27-1.jpg"),
          description:
            "Precisión y Confiabilidad para el Quirófano. El Monitor quirúrgico IPS Full HD de 27» de LG ha sido diseñado para ofrecer una visualización de alta calidad en entornos quirúrgicos, proporcionando imágenes nítidas y precisas para una mejor toma de decisiones en procedimientos médicos. Con una tecnología de pantalla IPS optimizada y características de seguridad avanzadas, este monitor es la opción ideal para quirófanos y centros de cirugía.",
          specifications: {
            Tamaño: "27 pulgadas",
            Resolución: "1920 x 1080 (Full HD)",
            Panel: "IPS",
            "Ángulo de Visión": "178° horizontal y vertical",
            "Cobertura de Color": "sRGB 115% (Rojo Intenso)",
            "Estabilización de Brillo": "Sensor integrado",
            Cumplimiento: "DICOM Parte 14",
            "Protección Frontal": "IP35",
            "Protección Cuerpo": "IP32",
            "Vidrio Protector": "Incluido para mayor durabilidad",
            "Tecnología de Protección": "Flicker Safe",
          },
          benefits: {
            "Calidad de Imagen Óptima para Cirugías de Precisión":
              "La pantalla IPS Full HD de 27 pulgadas ofrece imágenes detalladas y una reproducción de color excepcional. Su compatibilidad con otros dispositivos quirúrgicos Full HD permite a los cirujanos visualizar con precisión los tejidos y estructuras anatómicas.",
            "Representación de Colores Realista con sRGB 115% (Rojo Intenso)":
              "El espectro de color mejorado con sRGB 115% permite una visualización más precisa del rojo, color fundamental en procedimientos quirúrgicos, asegurando imágenes realistas y bien definidas.",
            "Estabilización del Brillo para Mayor Consistencia":
              "El monitor está calibrado según el estándar DICOM Parte 14, asegurando una escala de grises precisa y homogénea para una interpretación fiable en entornos quirúrgicos.",
          },
          workflow: {
            "Diseñado para el Quirófano":
              "Construido específicamente para resistir las condiciones exigentes del entorno quirúrgico con características de protección y durabilidad avanzadas.",
            "Protección Contra Polvo y Agua":
              "Los monitores quirúrgicos de LG están diseñados para resistir condiciones exigentes en el quirófano. Gracias a sus certificaciones IP35 en la parte frontal e IP32 en la parte trasera, son lavables y altamente resistentes a fluidos corporales y otros contaminantes.",
            "Vidrio Protector para Mayor Durabilidad":
              "El vidrio protector facilita la limpieza del monitor y mejora su resistencia, proporcionando seguridad adicional contra líquidos y partículas contaminantes.",
          },
          ergonomics: {
            "Comodidad y Seguridad Visual":
              "Diseñado para proporcionar una experiencia visual cómoda y segura durante procedimientos quirúrgicos prolongados.",
            "Tecnología Flicker Safe: Reducción de la Fatiga Visual":
              "El Flicker Safe minimiza el parpadeo de la pantalla, reduciendo el cansancio ocular durante procedimientos quirúrgicos prolongados. Esto, combinado con la tecnología IPS, permite una experiencia visual más cómoda y menos agotadora para el equipo médico.",
          },
          features: [
            "Panel IPS de 27 pulgadas con resolución Full HD",
            "Cobertura de color sRGB 115% con rojo intenso",
            "Ángulo de visión amplio de 178° horizontal y vertical",
            "Estabilización automática del brillo",
            "Cumplimiento con estándar DICOM Parte 14",
            "Certificación IP35 frontal e IP32 cuerpo",
            "Vidrio protector para mayor durabilidad",
            "Tecnología Flicker Safe para protección ocular",
            "Resistencia a fluidos corporales y contaminantes",
            "Facilidad de limpieza y desinfección",
            "Compatibilidad con dispositivos quirúrgicos Full HD",
            "Visualización precisa de tejidos y estructuras anatómicas",
            "Escala de grises precisa y homogénea",
            "Diseño optimizado para quirófanos",
          ],
          applications: [
            "Quirófanos y salas de cirugía",
            "Endoscopía y laparoscopía",
            "Monitoreo de procedimientos en tiempo real",
            "Centros de simulación y formación quirúrgica",
            "Cirugía mínimamente invasiva",
            "Procedimientos de precisión",
            "Visualización de imágenes médicas en tiempo real",
            "Salas de operaciones especializadas",
          ],
          purchase_info: {
            "Tecnología Confiable para Quirófanos":
              "Adquiere la mejor tecnología quirúrgica con la garantía y soporte de RIACS Health Chile, distribuidores oficiales de LG Medical Display.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades quirúrgicas",
          },
        },
        "17HQ701G": {
          name: "Detector de rayos X digital LG con a-Si TFT",
          category: "Detector de Rayos X",
          image: require("../assets/images/lg/Detector-Rayos/16-17HQ701G-detector-rayos-x-a-si-tft-2.jpg"),
          description:
            "Tecnología Avanzada para Diagnósticos Precisos. El Detector de rayos X digital LG con a-Si TFT es una solución innovadora diseñada para proporcionar imágenes radiológicas de alta calidad en tiempo real. Gracias a su avanzada tecnología TFT de silicio amorfo, este detector garantiza una captación eficiente de imágenes con un rendimiento optimizado para hospitales y centros de diagnóstico.",
          specifications: {
            Tecnología: "TFT de silicio amorfo (a-Si)",
            "Tamaño de Píxel": "140 micrómetros",
            "Conversión A/D": "16 bits de precisión",
            "Tiempo de Transmisión": "1.5s (con cable) / 2.5s (inalámbrico)",
            Certificación: "IP68",
            Peso: "3.7 kg (8.2 lbs)",
            "Material del Cuerpo": "Carbono y Magnesio",
            "Carga Uniforme Máxima": "400 kg",
            "Carga Puntual Máxima": "200 kg",
            "Resistencia a Caídas": "Hasta 1.5 metros",
            "Autonomía de Batería": "Hasta 7.5 horas",
            "Intercambio en Caliente": "Hasta 1 minuto",
            Pantalla: "OLED incorporada",
            Almacenamiento: "Local integrado",
          },
          benefits: {
            "Imágenes Nítidas con Tecnología TFT de Silicio Amorfo":
              "Gracias a su tecnología TFT a-Si, este detector proporciona imágenes de alta precisión con excelente claridad y reducción de ruido, lo que facilita un diagnóstico más rápido y eficiente.",
            "Diseño Duradero y Resistente":
              "El Detector de rayos X digital LG con a-Si TFT ha sido diseñado para soportar las condiciones más exigentes con certificación IP68: Protección completa contra el polvo y resistencia al agua hasta 1 metro de profundidad por 30 minutos.",
            "Portabilidad Eficiente y Diseño Liviano":
              "Con un peso de solo 3.7 kg (8.2 lbs) y cuerpo de carbono y magnesio, este detector es fácil de manejar y transportar sin comprometer la resistencia estructural.",
          },
          workflow: {
            "Uso Continuo para Máximo Rendimiento":
              "Diseñado para proporcionar un rendimiento óptimo en ambientes de alta exigencia con características avanzadas de autonomía y eficiencia.",
            "Batería de Larga Duración para Horas de Trabajo Ininterrumpido":
              "Hasta 7.5 horas de autonomía, con un rendimiento óptimo en ambientes de alta exigencia. Intercambio en caliente hasta 1 minuto, permitiendo el cambio de batería sin interrupciones en el flujo de trabajo.",
            "Alta Velocidad de Transmisión":
              "Imágenes en 1.5s (con cable) / 2.5s (inalámbrico), asegurando un flujo de trabajo eficiente. Almacenamiento local, permitiendo la captura y gestión de imágenes directamente en el detector para mayor seguridad y accesibilidad.",
          },
          ergonomics: {
            "Conveniencia Mejorada para el Profesional de la Salud":
              "Diseñado con características que mejoran la experiencia del usuario y optimizan el flujo de trabajo médico.",
            "Pantalla OLED Incorporada":
              "Supervisión en tiempo real del estado del dispositivo mediante su panel OLED, permitiendo un monitoreo rápido y preciso del funcionamiento del detector.",
            "Resistencia Excepcional":
              "Carga Uniforme Máxima de 400 kg y Carga Puntual Máxima de 200 kg, garantizando su estabilidad en uso hospitalario. Pruebas de caída de hasta 1.5 metros, asegurando su resistencia en entornos clínicos dinámicos.",
          },
          features: [
            "Tecnología TFT de silicio amorfo (a-Si) avanzada",
            "Tamaño de píxel de 140 micrómetros para alta resolución",
            "Conversión A/D de 16 bits para máxima precisión",
            "Certificación IP68 para protección completa",
            "Peso ligero de 3.7 kg con cuerpo de carbono y magnesio",
            "Batería de larga duración hasta 7.5 horas",
            "Intercambio en caliente de batería hasta 1 minuto",
            "Pantalla OLED incorporada para monitoreo",
            "Transmisión rápida: 1.5s cableado / 2.5s inalámbrico",
            "Almacenamiento local integrado",
            "Resistencia a caídas hasta 1.5 metros",
            "Carga uniforme máxima de 400 kg",
            "Carga puntual máxima de 200 kg",
            "Reducción de ruido avanzada",
            "Facilidad de manejo y transporte",
          ],
          applications: [
            "Radiología general y especializada",
            "Hospitales y clínicas",
            "Diagnóstico por imagen en emergencias",
            "Unidades móviles de rayos X",
            "Centros de salud con alta demanda",
            "Estudios radiológicos de rutina",
            "Diagnóstico en tiempo real",
            "Imagenología portátil",
            "Centros de diagnóstico especializados",
          ],
          purchase_info: {
            "Tecnología Avanzada en Detección de Rayos X":
              "Adquiere la mejor tecnología en detección de rayos X con la garantía y soporte de RIACS Health Chile, distribuidores oficiales de LG Medical Display.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades de diagnóstico por imagen",
          },
        },
        "10HQ701G": {
          name: "Detector de rayos X digital LG con pantalla TFT a-Si",
          category: "Detector de Rayos X",
          image: require("../assets/images/lg/Detector-Rayos/18-10HQ701G-detector-rayos-x-a-si-tft-1.jpg"),
          description:
            "Precisión y rendimiento en imágenes médicas. El Detector de rayos X digital LG con pantalla TFT a-Si ha sido diseñado para proporcionar imágenes médicas de alta calidad con tiempos de procesamiento rápidos y una estructura robusta. Gracias a su avanzada tecnología de silicio amorfo (a-Si), garantiza diagnósticos más precisos y eficientes en diferentes entornos clínicos.",
          specifications: {
            Tecnología: "TFT de silicio amorfo (a-Si)",
            "Tamaño de Píxel": "140 micrómetros",
            "Conversión A/D": "16 bits de precisión",
            "Tiempo de Transmisión": "1.5s (con cable) / 2.5s (inalámbrico)",
            Certificación: "IP68",
            Peso: "3.7 kg (8.2 lbs)",
            "Material del Cuerpo": "Fibra de carbono y magnesio",
            "Carga Uniforme Máxima": "400 kg",
            "Carga Puntual Máxima": "200 kg",
            "Resistencia a Caídas": "Hasta 1.5 metros",
            "Autonomía de Batería": "Hasta 7.5 horas",
            "Intercambio en Caliente": "Hasta 1 minuto",
            Pantalla: "OLED integrada",
            Almacenamiento: "Memoria interna hasta 200 imágenes",
            Protección: "Resistente al polvo y agua",
          },
          benefits: {
            "Calidad de imagen superior con tecnología TFT a-Si":
              "Obtención de imágenes claras y nítidas. Gracias a su tecnología TFT de silicio amorfo, este detector permite la captura de imágenes médicas con una claridad excepcional, asegurando un diagnóstico preciso en cada estudio radiológico.",
            "Procesamiento de imágenes en segundos":
              "El Detector de rayos X digital LG con pantalla TFT a-Si ofrece una velocidad de procesamiento excepcional, logrando generar imágenes en solo 1,5 segundos con cable y 2,5 segundos de forma inalámbrica, lo que optimiza el flujo de trabajo en hospitales y clínicas.",
            "Diseño duradero y ergonómico":
              "Resistencia extrema con certificación IP68. El dispositivo cuenta con protección IP68, lo que lo hace completamente resistente al polvo y al agua, garantizando un rendimiento fiable en entornos médicos exigentes, como unidades de emergencia y quirófanos.",
          },
          workflow: {
            "Portabilidad y facilidad de uso":
              "Con un cuerpo de fibra de carbono y magnesio, este detector es ligero (3,7 kg) y fácil de manipular, lo que permite una movilidad óptima dentro del área médica.",
            "Batería de larga duración con intercambio en caliente":
              "Este detector está equipado con una batería de hasta 7,5 horas de autonomía, además de contar con una función de intercambio en caliente, que permite reemplazar la batería sin interrumpir el uso del equipo.",
            "Almacenamiento automático para mayor conveniencia":
              "El detector cuenta con memoria interna, lo que le permite almacenar hasta 200 imágenes sin necesidad de conexión a un PC. Esto permite continuar capturando imágenes y transferirlas posteriormente cuando sea necesario.",
          },
          ergonomics: {
            "Soporta grandes cargas sin comprometer el rendimiento":
              "400 kg de carga uniforme. 200 kg de carga puntual. Resiste caídas de hasta 1,5 metros en condiciones controladas. Este nivel de durabilidad permite su uso seguro y eficiente en una variedad de aplicaciones clínicas.",
            "Tecnología avanzada para monitoreo en tiempo real":
              "Pantalla OLED integrada. El Detector de rayos X digital LG con pantalla TFT a-Si incorpora una pantalla OLED, que permite a los profesionales de la salud visualizar información clave sobre el estado del dispositivo en tiempo real.",
            "Información en tiempo real":
              "Estado de conexión, nivel de batería restante, modo de almacenamiento, configuración de punto de acceso (AP). Supervisión completa del funcionamiento del detector para máxima eficiencia operativa.",
          },
          features: [
            "Tecnología TFT de silicio amorfo (a-Si) avanzada",
            "Tamaño de píxel de 140 micrómetros para alta resolución",
            "Conversión A/D de 16 bits para máxima precisión",
            "Certificación IP68 para protección completa",
            "Peso ligero de 3.7 kg con cuerpo de fibra de carbono y magnesio",
            "Batería de larga duración hasta 7.5 horas",
            "Intercambio en caliente de batería hasta 1 minuto",
            "Pantalla OLED integrada para monitoreo en tiempo real",
            "Transmisión rápida: 1.5s cableado / 2.5s inalámbrico",
            "Memoria interna para almacenar hasta 200 imágenes",
            "Resistencia a caídas hasta 1.5 metros",
            "Carga uniforme máxima de 400 kg",
            "Carga puntual máxima de 200 kg",
            "Resistente al polvo y agua con protección IP68",
            "Facilidad de manejo y transporte optimizada",
          ],
          applications: [
            "Radiología general y diagnóstico por imágenes",
            "Atención en unidades de emergencia y cuidados intensivos",
            "Salas de operaciones y procedimientos invasivos",
            "Uso en entornos clínicos con alta demanda de imágenes precisas",
            "Hospitales y clínicas especializadas",
            "Centros de diagnóstico por imagen",
            "Unidades móviles de rayos X",
            "Estudios radiológicos de rutina",
            "Diagnóstico en tiempo real",
            "Imagenología portátil",
          ],
          purchase_info: {
            "Tecnología Avanzada en Detección de Rayos X":
              "Adquiere la mejor tecnología en detección de rayos X con la garantía y soporte de RIACS Health Chile, distribuidores oficiales de LG Medical Display.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades de diagnóstico por imagen",
          },
        },
        "14HQ701G": {
          name: "Detector de rayos X digital LG con pantalla TFT a-Si",
          category: "Detector de Rayos X",
          image: require("../assets/images/lg/Detector-Rayos/18-10HQ701G-detector-rayos-x-a-si-tft-1.jpg"),
          description:
            "Tecnología Avanzada para Diagnósticos Precisos. El Detector de rayos X digital LG con pantalla TFT a-Si es una solución innovadora diseñada para proporcionar imágenes radiológicas de alta calidad en tiempo real. Gracias a su avanzada tecnología TFT de silicio amorfo, este detector garantiza una captación eficiente de imágenes con un rendimiento optimizado para hospitales y centros de diagnóstico.",
          specifications: {
            Tecnología: "TFT de silicio amorfo (a-Si)",
            "Tamaño de Píxel": "140 micrómetros",
            "Conversión A/D": "16 bits de precisión",
            "Tiempo de Transmisión": "1.5s (con cable) / 2s (inalámbrico)",
            Certificación: "IP68",
            Peso: "3.2 kg (7.1 lbs)",
            "Material del Cuerpo": "Carbono y Magnesio",
            Diseño: "Bisel en los cuatro lados",
            "Carga Uniforme Máxima": "400 kg",
            "Carga Puntual Máxima": "200 kg",
            "Resistencia a Caídas": "Hasta 1.5 metros",
            "Autonomía de Batería": "Hasta 7.5 horas",
            "Intercambio en Caliente": "Hasta 1 minuto",
            Pantalla: "OLED incorporada",
            Almacenamiento: "Local integrado",
            Protección: "Resistente al polvo y agua hasta 1 metro por 30 minutos",
          },
          benefits: {
            "Imágenes Nítidas con Tecnología TFT de Silicio Amorfo":
              "Obtenga Imágenes Claras con Alta Definición. Gracias a su tecnología TFT a-Si, este detector proporciona imágenes de alta precisión con excelente claridad y reducción de ruido, lo que facilita un diagnóstico más rápido y eficiente.",
            "Diseño Duradero y Resistente":
              "El Detector de rayos X digital LG con pantalla TFT a-Si ha sido diseñado para soportar las condiciones más exigentes: Certificación IP68: Protección completa contra el polvo y resistencia al agua hasta 1 metro de profundidad por 30 minutos.",
            "Portabilidad Eficiente y Diseño Liviano":
              "Cuerpo de Carbono y Magnesio: Ligero y Robusto. Con un peso de solo 3,2 kg (7,1 lbs) y bisel en los cuatro lados, este detector es fácil de manejar y transportar sin comprometer la resistencia estructural.",
          },
          workflow: {
            "Uso Continuo para Máximo Rendimiento":
              "Batería de Larga Duración para Horas de Trabajo Ininterrumpido. Hasta 7,5 horas de autonomía, con un rendimiento óptimo en ambientes de alta exigencia.",
            "Intercambio en Caliente para Flujo de Trabajo Continuo":
              "Intercambio en caliente hasta 1 minuto, permitiendo el cambio de batería sin interrupciones en el flujo de trabajo.",
            "Alta Velocidad de Transmisión":
              "Imágenes en 1,5 s (con cable) / 2 s (inalámbrico), asegurando un flujo de trabajo eficiente. Almacenamiento local, permitiendo la captura y gestión de imágenes directamente en el detector para mayor seguridad y accesibilidad.",
          },
          ergonomics: {
            "Conveniencia Mejorada para el Profesional de la Salud":
              "Pantalla OLED Incorporada. Supervisión en tiempo real del estado del dispositivo mediante su panel OLED, permitiendo un monitoreo rápido y preciso.",
            "Resistencia Excepcional":
              "Carga Uniforme Máxima de 400 kg y Carga Puntual Máxima de 200 kg, garantizando su estabilidad en uso hospitalario. Pruebas de caída de hasta 1,5 metros, asegurando su resistencia en entornos clínicos dinámicos.",
            "Diseño Ergonómico Optimizado":
              "Bisel en los cuatro lados para facilitar el manejo y agarre seguro durante procedimientos médicos, mejorando la experiencia del usuario.",
          },
          features: [
            "Tecnología TFT de silicio amorfo (a-Si) avanzada",
            "Tamaño de píxel de 140 micrómetros para alta resolución",
            "Conversión A/D de 16 bits para máxima precisión",
            "Certificación IP68 para protección completa",
            "Peso ligero de 3.2 kg con cuerpo de carbono y magnesio",
            "Diseño con bisel en los cuatro lados",
            "Batería de larga duración hasta 7.5 horas",
            "Intercambio en caliente de batería hasta 1 minuto",
            "Pantalla OLED incorporada para monitoreo",
            "Transmisión rápida: 1.5s cableado / 2s inalámbrico",
            "Almacenamiento local integrado",
            "Resistencia a caídas hasta 1.5 metros",
            "Carga uniforme máxima de 400 kg",
            "Carga puntual máxima de 200 kg",
            "Protección contra polvo y agua hasta 1 metro",
            "Reducción de ruido avanzada",
            "Facilidad de manejo y transporte",
          ],
          applications: [
            "Radiología general y especializada",
            "Hospitales y clínicas",
            "Diagnóstico por imagen en emergencias y unidades móviles",
            "Centros de salud con alta demanda de estudios radiológicos",
            "Unidades de cuidados intensivos",
            "Centros de diagnóstico por imagen",
            "Estudios radiológicos de rutina",
            "Diagnóstico en tiempo real",
            "Imagenología portátil",
            "Centros médicos especializados",
          ],
          purchase_info: {
            "Tecnología Avanzada en Detección de Rayos X":
              "Adquiere la mejor tecnología en detección de rayos X con la garantía y soporte de RIACS Health Chile, distribuidores oficiales de LG Medical Display.",
            Financiamiento: "Opciones de financiamiento hasta 12 meses con tarjeta de crédito",
            "Distribuidor exclusivo":
              "RIACS Health Chile, distribuidores oficiales de LG Medical Display",
            Soporte: "Asistencia técnica especializada y soporte 24/7",
            Contacto:
              "Contáctanos para recibir asesoría personalizada y una cotización adaptada a tus necesidades de diagnóstico por imagen",
          },
        },
      },
    };
  },
  mounted() {
    console.log("Estado inicial de sidenavOpen:", this.sidenavOpen);
    // Agregar animaciones al hacer scroll
    this.observeElements();
    // Prevenir activación accidental del sidenav
    this.preventAccidentalSidenavActivation();
  },
  methods: {
    openSidenav(productId, event) {
      // Verificar que el click fue intencional (no accidental)
      if (event && event.isTrusted === false) {
        return; // Ignorar eventos sintéticos
      }

      // Pequeño delay para evitar clicks accidentales
      setTimeout(() => {
        this.selectedProduct = this.products[productId];
        this.sidenavOpen = true;
        console.log('Abriendo sidenav, sidenavOpen:', this.sidenavOpen);
        this.showQuoteForm = false; // Reset quote form when opening sidenav
        this.resetQuoteForm(); // Clear form data
        // Prevenir scroll del body cuando el sidenav está abierto
        document.body.style.overflow = "hidden";
      }, 50);
    },
    closeSidenav() {
      this.sidenavOpen = false;
      console.log('Cerrando sidenav, sidenavOpen:', this.sidenavOpen);
      this.selectedProduct = null;
      this.showQuoteForm = false;
      this.resetQuoteForm();
      // Restaurar scroll del body
      document.body.style.overflow = "auto";
    },
    toggleQuoteForm() {
      this.showQuoteForm = !this.showQuoteForm;
      if (this.showQuoteForm && this.selectedProduct) {
        this.quoteForm.producto = this.selectedProduct.name;
      }
    },
    async submitQuote() {
      // Validar que todos los campos requeridos estén llenos
      if (!this.quoteForm.nombre || !this.quoteForm.telefono || !this.quoteForm.correo) {
        alert("Por favor, complete todos los campos requeridos.");
        return;
      }

      try {
        this.isSubmittingQuote = true;

        const token = await this.getToken();

        const emailBody = {
          Name: this.quoteForm.nombre,
          Email: this.quoteForm.correo,
          Phone: this.quoteForm.telefono,
          Comment: `Solicitud de cotización para: ${
            this.quoteForm.producto
          }\n\nComentarios adicionales: ${this.quoteForm.comentario || "Ninguno"}`,
        };

        const response = await fetch("https://webapi.optimuz.cl/WEBAPI/api/v2/email/send", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Origin: "http://localhost:8080",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(emailBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.Comment || "Error al enviar la cotización");
        }

        alert("¡Cotización enviada exitosamente! Nos pondremos en contacto contigo pronto.");
        this.resetQuoteForm();
        this.showQuoteForm = false;
      } catch (error) {
        console.error("Error:", error);
        alert("Hubo un error al enviar la cotización. Por favor, intenta nuevamente.");
      } finally {
        this.isSubmittingQuote = false;
      }
    },
    resetQuoteForm() {
      this.quoteForm = {
        nombre: "",
        telefono: "",
        correo: "",
        comentario: "",
        producto: this.selectedProduct ? this.selectedProduct.name : "",
      };
    },
    async getToken() {
      try {
        const response = await fetch("https://webapi.optimuz.cl/WEBAPI/api/v2/email/tokenjwt", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Origin: "http://localhost:8080",
          },
          body: JSON.stringify({
            email: API_CREDENTIALS.email,
            password: API_CREDENTIALS.password,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error al obtener el token");
        }

        const data = await response.json();
        return data.token;
      } catch (error) {
        console.error("Error detallado:", error);
        throw error;
      }
    },
    observeElements() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate-on-scroll");
            }
          });
        },
        {
          threshold: 0.1,
        }
      );

      // Observar elementos con clase sectionAnimate
      document.querySelectorAll(".sectionAnimate").forEach((el) => {
        observer.observe(el);
      });
    },
    preventAccidentalSidenavActivation() {
      // Método simplificado - el display:none debería resolver el problema principal
      console.log("Sistema de prevención de activación accidental inicializado");
    },
  },
};
</script>

<style scoped>
.catalogo-medico-lg {
  font-family: "Inter", sans-serif;
}

/* Animaciones */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-down {
  animation: fadeInDown 1s ease-out;
}

.animate-fade-in-up {
  animation: fadeInUp 1s ease-out;
}

.sectionAnimate {
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s ease-out;
}

.animate-on-scroll {
  opacity: 1;
  transform: translateY(0);
}

/* Hover effects */
.bg-white:hover {
  transform: translateY(-2px);
  transition: transform 0.3s ease;
}

/* Sidenav Styles - Removed duplicate styles, using Tailwind classes instead */

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
}

.close-btn:hover {
  background-color: #f3f4f6;
}

.product-image {
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: 0.5rem;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.spec-item:last-child {
  border-bottom: none;
}

.feature-item,
.application-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.feature-item::before,
.application-item::before {
  content: "✓";
  color: #10b981;
  font-weight: bold;
  margin-right: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .text-5xl {
    font-size: 2.5rem;
  }

  .text-4xl {
    font-size: 2rem;
  }

  .sidenav {
    max-width: 100%;
  }
}
</style>
